import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRightIcon,
  ArrowsPointingOutIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  ScaleIcon,
  Squares2X2Icon,
  TrashIcon,
  TrophyIcon,
  UserPlusIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { MOCK_LOTS } from './data/mockData';
import { computeEnterpriseStats } from './utils/stats';
import type { EnterpriseStats, PriceFormula } from './utils/stats';
import { computeCellAlerts, computeEcartFlags, computeOutlierFlags } from './utils/alerts';
import type { AlertLevel } from './utils/alerts';
import type {
  BpuSheet,
  Enterprise,
  FilterKey,
  Lot,
  Offer,
  OfferStatus,
  OrphanLine,
  PanelContent,
  RefLine,
  RefPrestation,
  ViewMode,
} from './types';

// Onglet récap : affiché en premier quand la consultation contient plusieurs lots.
const RECAP_ID = '__recap__';
const showRecapTab = MOCK_LOTS.length > 1;

// Pour un BPU : applique les qtys de la DQE active au catalogue et recompose les offers.
// Pour un DPGF : renvoie les lines telles quelles.
function buildViewLines(lot: Lot, sheet: BpuSheet | null): { lines: RefLine[]; orphans: OrphanLine[] } {
  if (lot.type === 'DPGF' || !sheet) {
    return { lines: lot.lines, orphans: lot.orphans };
  }
  const viewLines: RefLine[] = lot.lines.map(line => {
    if (line.isSection) return line;
    const qty = sheet.quantities[line.id] ?? 0;
    const newOffers: Record<string, Offer | null> = {};
    for (const name of Object.keys(line.offers)) {
      const offer = line.offers[name];
      if (offer === null) {
        newOffers[name] = null;
      } else {
        newOffers[name] = { ...offer, qty, total: qty * offer.pu };
      }
    }
    return { ...line, refQty: qty, offers: newOffers };
  });
  return { lines: viewLines, orphans: lot.orphans };
}

const fmt = (n?: number | null): string => (n != null ? n.toLocaleString('fr-FR') + ' €' : '—');
const fmtNum = (n?: number | null): string => (n != null ? n.toLocaleString('fr-FR') : '—');
const fmtCompact = (n: number): string => {
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1).replace('.', ',') + ' k€';
  return n.toLocaleString('fr-FR') + ' €';
};

// ---------- EcartBar ----------
interface EcartBarProps {
  ecart?: number | null;
}

const EcartBar: React.FC<EcartBarProps> = ({ ecart }) => {
  if (ecart == null) return <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>;
  const abs = Math.min(Math.abs(ecart), 20);
  const isPositive = ecart > 0;
  const color = Math.abs(ecart) > 10 ? '#DC2626' : Math.abs(ecart) > 5 ? '#D97706' : '#059669';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 60 }}>
      <div style={{ width: 26, height: 5, background: 'var(--border)', borderRadius: 3, position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 0, height: '100%', left: isPositive ? '50%' : `${50 - abs * 2.5}%`, width: `${abs * 2.5}%`, background: color, borderRadius: 3 }} />
        <div style={{ position: 'absolute', left: '50%', top: 0, width: 1, height: '100%', background: 'var(--text-secondary)', opacity: 0.25 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>{ecart > 0 ? '+' : ''}{ecart.toFixed(1)}%</span>
    </div>
  );
};

// ---------- Confidence (texte coloré, pas de pill) ----------
interface ConfidenceProps {
  confidence?: number | null;
}

const Confidence: React.FC<ConfidenceProps> = ({ confidence }) => {
  if (confidence == null) return null;
  const pct = Math.round(confidence * 100);
  const color = pct >= 70 ? '#059669' : pct >= 40 ? '#D97706' : '#DC2626';
  return <span style={{ fontSize: 12, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>;
};

// ---------- StatusIcon (Heroicons) ----------
interface StatusIconProps {
  status?: OfferStatus;
}

const StatusIcon: React.FC<StatusIconProps> = ({ status }) => {
  if (status === 'suspect') return <ExclamationTriangleIcon style={{ width: 14, height: 14, color: '#D97706', display: 'inline-block', verticalAlign: '-2px' }} />;
  if (status === 'alert') return <ExclamationTriangleIcon style={{ width: 14, height: 14, color: "#DC2626", display: 'inline-block', verticalAlign: '-2px' }} />;
  return null;
};

// ---------- AlertDot ----------
// Minimal visual cue : petit point rouge (irrégularité) ou orange (variance).
// On garde l'indicateur discret pour éviter l'effet sapin de Noël.
const AlertDot: React.FC<{ level: AlertLevel; size?: number }> = ({ level, size = 6 }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      background: level === 'red' ? '#DC2626' : '#D97706',
      flexShrink: 0,
    }}
  />
);

// ---------- DesignationCompare ----------
interface DesignationCompareProps {
  refDesignation: string;
  offerDesignation: string;
  isSuspect?: boolean;
}

const DesignationCompare: React.FC<DesignationCompareProps> = ({ refDesignation, offerDesignation, isSuspect }) => {
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 8, marginBottom: 8,
      background: isSuspect ? '#D9770608' : 'var(--card-bg)',
      border: isSuspect ? '1px solid #D9770625' : '1px solid transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 2 }}>Réf</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.3 }}>{refDesignation}</div>
        </div>
        <ArrowRightIcon style={{ width: 14, height: 14, color: 'var(--muted)', marginTop: 14, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 2 }}>Offre</div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.3 }}>{offerDesignation}</div>
        </div>
      </div>
    </div>
  );
};

// ---------- PanelEnterprise ----------
interface PanelEnterpriseProps {
  enterprise: Enterprise;
  lotOrphans: OrphanLine[];
  stats?: EnterpriseStats;
  totalEnterprises: number;
  isRawBpu?: boolean;
  scale: number;
  onClose: () => void;
}

const PanelEnterprise: React.FC<PanelEnterpriseProps> = ({ enterprise, lotOrphans, stats, totalEnterprises, isRawBpu, scale, onClose }) => {
  const orphans = lotOrphans.filter(o => o.enterprise === enterprise.name);
  const ecartAbs = stats ? Math.abs(stats.ecartPct) : 0;
  const ecartColor = ecartAbs > 5 ? '#DC2626' : ecartAbs > 1 ? '#D97706' : '#059669';
  type Metric = { label: string; value: string; color: string; sub?: React.ReactNode };
  const metrics: Metric[] = isRawBpu ? [
    { label: 'Catalogue', value: 'BPU', color: 'var(--text-primary)', sub: <span style={{ color: 'var(--muted)', fontSize: 11, fontStyle: 'italic' }}>Sélectionnez une DQE pour totaliser</span> },
    { label: 'Correspondance', value: enterprise.matchRate + '%', color: enterprise.matchRate > 90 ? '#059669' : '#D97706' },
  ] : stats ? [
    { label: 'Total déclaré', value: fmt(stats.totalDeclare), color: 'var(--text-primary)' },
    {
      label: 'Total recalculé',
      value: fmt(stats.totalRecalcule),
      color: ecartAbs > 1 ? ecartColor : 'var(--text-primary)',
      sub: stats.ecart !== 0 ? (
        <span style={{ color: ecartColor, fontSize: 11, fontWeight: 500 }}>
          Écart {stats.ecart > 0 ? '+' : ''}{fmtCompact(stats.ecart)} ({stats.ecartPct > 0 ? '+' : ''}{stats.ecartPct.toFixed(2)}%)
        </span>
      ) : undefined,
    },
    (() => {
      const scoreColor = stats.rank === 1 ? '#059669' : 'var(--text-primary)';
      const metric: Metric = {
        label: `Note financière · Rang ${stats.rank}/${totalEnterprises}`,
        value: stats.score.toFixed(1) + ' /' + scale,
        color: scoreColor,
        sub: stats.rank === 1 ? (
          <span style={{ color: '#059669', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <TrophyIcon style={{ width: 12, height: 12 }} /> Meilleure offre
          </span>
        ) : undefined,
      };
      return metric;
    })(),
    {
      label: 'Correspondance',
      value: enterprise.matchRate + '%',
      color: enterprise.matchRate > 90 ? '#059669' : '#D97706',
    },
  ] : [
    { label: 'Total HT', value: fmt(enterprise.totalHT), color: 'var(--text-primary)' },
    { label: 'Correspondance', value: enterprise.matchRate + '%', color: enterprise.matchRate > 90 ? '#059669' : '#D97706' },
  ];
  const actionIconStyle = { width: 16, height: 16, flexShrink: 0 };
  const actions: Array<{ Icon: React.FC<React.SVGProps<SVGSVGElement>>; label: string; dashed?: boolean; danger?: boolean }> = [
    { Icon: ArrowUpTrayIcon, label: 'Importer une nouvelle version', dashed: true },
    { Icon: PencilSquareIcon, label: 'Générer le retour entreprise' },
    { Icon: EyeIcon, label: 'Voir uniquement cette offre' },
    { Icon: TrashIcon, label: 'Retirer cette entreprise', danger: true },
  ];
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: enterprise.color }} />
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{enterprise.name}</h3>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Version {enterprise.version} · {enterprise.imported}</p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, display: 'flex' }}>
          <XMarkIcon style={{ width: 18, height: 18 }} />
        </button>
      </div>
      {stats?.isOAB && (
        <div style={{
          padding: '12px 14px', borderRadius: 10, marginBottom: 16,
          background: '#DC262608', border: '1px solid #DC262640',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <ExclamationTriangleIcon style={{ width: 18, height: 18, color: "#DC2626", flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#C2410C' }}>Offre anormalement basse (OAB)</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.4 }}>
              Détectée par méthode double-moyenne 20%. Une justification du prix est requise (art. R. 2152-3 CCP).
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
        {metrics.map(item => (
          <div key={item.label} style={{ padding: 14, borderRadius: 10, background: 'var(--card-bg)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</div>
            <div style={{ fontSize: 19, fontWeight: 700, color: item.color, fontVariantNumeric: 'tabular-nums' }}>{item.value}</div>
            {item.sub && <div style={{ marginTop: 4 }}>{item.sub}</div>}
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Fichier source</div>
        <div style={{ padding: 12, borderRadius: 8, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <DocumentTextIcon style={{ width: 20, height: 20, color: 'var(--text-secondary)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{enterprise.file}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Importé le {enterprise.imported}</div>
          </div>
        </div>
      </div>
      {orphans.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            Lignes ajoutées <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>· {orphans.length}</span>
          </div>
          {orphans.map(o => (
            <div key={o.id} style={{ padding: 12, borderRadius: 8, border: '1px solid #DC262630', marginBottom: 6, background: '#DC262606' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{o.designation}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>{fmtNum(o.qty)} {o.unit} × {fmt(o.pu)} = {fmt(o.total)}</div>
              {o.suggestion ? (
                <div style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ArrowRightIcon style={{ width: 12, height: 12, color: 'var(--text-secondary)', flexShrink: 0 }} />
                    <span style={{ fontWeight: 500 }}>{o.suggestion.refDesignation}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Confidence confidence={o.suggestion.confidence} />
                    <button style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #059669', background: '#05966912', color: '#059669', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Accepter</button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>Aucune suggestion — matcher manuellement</div>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Actions</div>
      {actions.map(({ Icon, label, dashed, danger }) => (
        <button key={label} style={{
          padding: '10px 14px', borderRadius: 8, width: '100%', marginBottom: 6, textAlign: 'left',
          border: dashed ? '1px dashed var(--border)' : danger ? '1px solid #DC262633' : '1px solid var(--border)',
          background: 'transparent', cursor: 'pointer', fontSize: 13,
          color: danger ? '#DC2626' : 'var(--text-primary)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}><Icon style={actionIconStyle} /> {label}</button>
      ))}
    </div>
  );
};

// ---------- PanelRow ----------
interface PanelRowProps {
  row: RefPrestation;
  enterprises: Enterprise[];
  optional: boolean;
  onToggleOptional: () => void;
  isRawBpu?: boolean;
  onClose: () => void;
}

const PanelRow: React.FC<PanelRowProps> = ({ row, enterprises, optional, onToggleOptional, isRawBpu, onClose }) => {
  const refMetrics: Array<{ l: string; v: string }> = isRawBpu
    ? [
        { l: 'Unité', v: row.refUnit },
        { l: 'PU Réf', v: fmt(row.refPU) },
      ]
    : [
        { l: 'Qté', v: `${fmtNum(row.refQty)} ${row.refUnit}` },
        { l: 'PU', v: fmt(row.refPU) },
        { l: 'Total', v: fmt(row.refQty * row.refPU) },
      ];
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 20 }}>
        <div>
          <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--text-secondary)', fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>Art. {row.art}</p>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{row.designation}</h3>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, display: 'flex' }}>
          <XMarkIcon style={{ width: 18, height: 18 }} />
        </button>
      </div>

      <div style={{ padding: 14, borderRadius: 10, background: 'var(--card-bg)', marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Référence</div>
        <div style={{ display: 'flex', gap: 20 }}>
          {refMetrics.map(item => (
            <div key={item.l}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item.l}</span>
              <br />
              <span style={{ fontWeight: 600, fontSize: 14 }}>{item.v}</span>
            </div>
          ))}
        </div>
      </div>

      <label style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
        border: '1px solid var(--border)', marginBottom: 20, cursor: 'pointer',
        background: optional ? '#F3F4F6' : 'transparent',
      }}>
        <input
          type="checkbox"
          checked={optional}
          onChange={onToggleOptional}
          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#7C3AED' }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Ligne optionnelle</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Non comptée dans les totaux</div>
        </div>
      </label>

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Offres reçues</div>
      {enterprises.map(ent => {
        const offer = row.offers[ent.name];
        const isSuspect = offer?.status === 'suspect';
        const isAlert = offer?.status === 'alert';
        return (
          <div key={ent.name} style={{
            padding: 14, borderRadius: 10, marginBottom: 10,
            border: `1px solid ${isAlert ? '#DC262650' : isSuspect ? '#D9770650' : 'var(--border)'}`,
            background: isAlert ? '#DC262608' : isSuspect ? '#D9770606' : 'transparent',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: ent.color }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{ent.name}</span>
              </div>
              {offer && <StatusIcon status={offer.status} />}
            </div>

            {offer ? (
              <>
                <DesignationCompare
                  refDesignation={row.designation}
                  offerDesignation={offer.designation}
                  isSuspect={isSuspect}
                />

                {(() => {
                  const recalc = offer.qty * offer.pu;
                  const totalMismatch = Math.abs(recalc - offer.total) > 0.5;
                  const qtyWarn = !isRawBpu && offer.qty !== row.refQty;
                  if (isRawBpu) {
                    return (
                      <div style={{ display: 'flex', gap: 16, marginBottom: 8, paddingLeft: 2, alignItems: 'flex-end' }}>
                        <div>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>PU</span>
                          <br />
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{fmt(offer.pu)} <span style={{ fontSize: 11, color: 'var(--muted)' }}>/ {row.refUnit}</span></span>
                        </div>
                        <div style={{ marginLeft: 'auto', paddingBottom: 2 }}>
                          <EcartBar ecart={offer.ecart} />
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8, paddingLeft: 2, alignItems: 'flex-end' }}>
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Qté</span>
                        <br />
                        <span style={{ fontWeight: 500, fontSize: 14, color: qtyWarn ? '#D97706' : 'var(--text-primary)' }}>{fmtNum(offer.qty)} {row.refUnit}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>PU</span>
                        <br />
                        <span style={{ fontWeight: 500, fontSize: 14 }}>{fmt(offer.pu)}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Total</span>
                        <br />
                        <span style={{ fontWeight: 600, fontSize: 14, color: totalMismatch ? '#D97706' : 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {fmt(recalc)}
                          {totalMismatch && <ExclamationTriangleIcon style={{ width: 12, height: 12 }} />}
                        </span>
                        {totalMismatch && (
                          <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>
                            Annoncé : <span style={{ textDecoration: 'line-through' }}>{fmt(offer.total)}</span>
                          </div>
                        )}
                      </div>
                      <div style={{ marginLeft: 'auto', paddingBottom: 2 }}>
                        <EcartBar ecart={offer.ecart} />
                      </div>
                    </div>
                  );
                })()}

                {offer.alert && (
                  <div style={{ padding: '6px 10px', borderRadius: 6, background: '#D9770614', fontSize: 12, color: '#92400E', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ExclamationTriangleIcon style={{ width: 14, height: 14, flexShrink: 0 }} /> {offer.alert}
                  </div>
                )}

                {isSuspect && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button style={{ flex: 1, padding: '7px 12px', borderRadius: 6, border: '1px solid #059669', background: '#05966912', color: '#059669', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <CheckCircleIcon style={{ width: 14, height: 14 }} /> Confirmer
                    </button>
                    <button style={{ flex: 1, padding: '7px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Corriger le matching</button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic', padding: '4px 0' }}>Ligne absente de cette offre</div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {([{ Icon: LinkIcon, label: 'Matcher manuellement' }, { Icon: ChatBubbleLeftEllipsisIcon, label: 'Ajouter un commentaire' }] as const).map(a => (
          <button key={a.label} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
            <a.Icon style={{ width: 16, height: 16, flexShrink: 0 }} /> {a.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ---------- PanelOrphan ----------
interface PanelOrphanProps {
  orphan: OrphanLine;
  enterprises: Enterprise[];
  lines: RefLine[];
  onClose: () => void;
}

const PanelOrphan: React.FC<PanelOrphanProps> = ({ orphan, enterprises, lines, onClose }) => {
  const ent = enterprises.find(e => e.name === orphan.enterprise);
  const possibleMatches = lines.filter((r): r is RefPrestation => !r.isSection && r.refUnit === orphan.unit).slice(0, 4);
  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            <XCircleIcon style={{ width: 14, height: 14, color: '#DC2626', flexShrink: 0 }} />
            <span>Ligne ajoutée</span>
            <span style={{ color: 'var(--border)' }}>·</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: ent?.color, fontWeight: 500 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: ent?.color, display: 'inline-block' }} />{orphan.enterprise}
            </span>
          </div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{orphan.designation}</h3>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, display: 'flex' }}>
          <XMarkIcon style={{ width: 18, height: 18 }} />
        </button>
      </div>

      <div style={{ padding: 14, borderRadius: 10, background: 'var(--card-bg)', marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Données de l'offre</div>
        <div style={{ display: 'flex', gap: 20 }}>
          {[
            { l: 'Qté', v: `${fmtNum(orphan.qty)} ${orphan.unit}` },
            { l: 'PU', v: fmt(orphan.pu) },
            { l: 'Total', v: fmt(orphan.total) },
          ].map(item => (
            <div key={item.l}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{item.l}</span>
              <br />
              <span style={{ fontWeight: 600, fontSize: 14 }}>{item.v}</span>
            </div>
          ))}
        </div>
      </div>

      {orphan.suggestion && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Suggestion automatique</span>
            <Confidence confidence={orphan.suggestion.confidence} />
          </div>
          <div style={{
            padding: 12, borderRadius: 10,
            border: orphan.suggestion.confidence >= 0.7 ? '1px solid #05966940' : '1px solid #D9770640',
            background: orphan.suggestion.confidence >= 0.7 ? '#05966906' : '#D9770606',
          }}>
            <DesignationCompare
              refDesignation={orphan.suggestion.refDesignation}
              offerDesignation={orphan.designation}
              isSuspect={orphan.suggestion.confidence < 0.7}
            />
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", marginBottom: 10, paddingLeft: 2 }}>
              Art. {orphan.suggestion.refId}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <CheckCircleIcon style={{ width: 15, height: 15 }} /> Accepter
              </button>
              <button style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Ignorer</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Matcher avec une ligne de la REF</div>
      <input
        placeholder="Rechercher dans la REF…"
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
          fontSize: 13, marginBottom: 10, background: 'var(--card-bg)', outline: 'none',
          boxSizing: 'border-box', color: 'var(--text-primary)',
        }}
      />
      {possibleMatches.map(m => (
        <div
          key={m.id}
          style={{
            padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--card-bg)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div>
            <span style={{ color: 'var(--text-secondary)', fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, marginRight: 8 }}>{m.art}</span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{m.designation}</span>
          </div>
          <button style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', fontSize: 11, fontWeight: 500, cursor: 'pointer', color: 'var(--text-secondary)', flexShrink: 0 }}>Matcher</button>
        </div>
      ))}

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <button style={{ padding: '10px 14px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          <PlusIcon style={{ width: 16, height: 16, flexShrink: 0 }} /> Créer une nouvelle ligne dans la REF
        </button>
      </div>
    </div>
  );
};

// ---------- ComparateurMain ----------
const rootStyle: React.CSSProperties = {
  '--bg': '#F9FAFB',          // gray-50
  '--card-bg': '#F3F4F6',     // gray-100
  '--border': '#E5E7EB',      // gray-200
  '--text-primary': '#111827', // gray-900
  '--text-secondary': '#6B7280', // gray-500
  '--muted': '#9CA3AF',        // gray-400
  '--accent': '#7C3AED',       // violet-600
  '--panel-bg': '#FFFFFF',
  background: 'var(--bg)',
  minHeight: '100vh',
  color: 'var(--text-primary)',
} as React.CSSProperties;

const thBase: React.CSSProperties = {
  padding: '10px 8px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textAlign: 'center',
  verticalAlign: 'bottom',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdBase: React.CSSProperties = {
  padding: '8px 8px',
  fontSize: 13,
  whiteSpace: 'nowrap',
};

// Colonnes REF figées à gauche (sticky) : largeurs resserrées + offsets cumulés.
const REF_W = { art: 42, desig: 200, qty: 55, unit: 32, pu: 70, total: 85 };
const REF_L = {
  art: 0,
  desig: REF_W.art,
  qty: REF_W.art + REF_W.desig,
  unit: REF_W.art + REF_W.desig + REF_W.qty,
  pu: REF_W.art + REF_W.desig + REF_W.qty + REF_W.unit,
  total: REF_W.art + REF_W.desig + REF_W.qty + REF_W.unit + REF_W.pu,
};
// Largeur totale des 6 colonnes REF (pour cellule footer colSpan=4 = Art+Désig+Qté+U).
const REF_LABEL_W = REF_W.art + REF_W.desig + REF_W.qty + REF_W.unit;

const ComparateurMain: React.FC = () => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelContent, setPanelContent] = useState<PanelContent | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<FilterKey>('all');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [viewMode, setViewMode] = useState<ViewMode>('detailed');
  const [activeEntTab, setActiveEntTab] = useState(0);
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [matchBannerDismissed, setMatchBannerDismissed] = useState(false);
  const [optionalIds, setOptionalIds] = useState<Set<string>>(new Set());
  const [hiddenEntsByLot, setHiddenEntsByLot] = useState<Record<string, Set<string>>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [entDropdownOpen, setEntDropdownOpen] = useState(false);
  const [activeLotId, setActiveLotId] = useState<string>(showRecapTab ? RECAP_ID : MOCK_LOTS[0].id);
  const [activeSheetId, setActiveSheetId] = useState<string | null>(() => {
    const first = MOCK_LOTS[0];
    return first.sheets && first.sheets.length > 0 ? first.sheets[0].id : null;
  });
  // Clés : `${lotId}` pour DPGF, `${lotId}:${sheetId}` pour chaque DQE d'un BPU.
  const [scaleByLot, setScaleByLot] = useState<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const l of MOCK_LOTS) {
      if (l.sheets && l.sheets.length > 0) {
        for (const s of l.sheets) m[`${l.id}:${s.id}`] = 60;
      } else {
        m[l.id] = 60;
      }
    }
    return m;
  });
  const [formulaByLot, setFormulaByLot] = useState<Record<string, PriceFormula>>(() => {
    const m: Record<string, PriceFormula> = {};
    for (const l of MOCK_LOTS) {
      if (l.sheets && l.sheets.length > 0) {
        for (const s of l.sheets) m[`${l.id}:${s.id}`] = 'inverse';
      } else {
        m[l.id] = 'inverse';
      }
    }
    return m;
  });
  const [scaleOpen, setScaleOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Ajustements par DQE : adjustByKey[scoreKey][lineId][entName] = { qty?, pu? }.
  // L'offre résultante = override ?? originale. Bulk = écrire la même valeur sur toutes les ents.
  const [adjustByKey, setAdjustByKey] = useState<Record<string, Record<string, Record<string, { qty?: number; pu?: number }>>>>({});
  // Top-N écarts orange à conserver par offre — stocké par DQE comme scale/formule.
  const [topNByLot, setTopNByLot] = useState<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const l of MOCK_LOTS) {
      if (l.sheets && l.sheets.length > 0) {
        for (const s of l.sheets) m[`${l.id}:${s.id}`] = 10;
      } else {
        m[l.id] = 10;
      }
    }
    return m;
  });

  // --- Mode Matching (correction manuelle du rapprochement offres ↔ réf) ---
  // Le matching initial reste automatique ; ce mode permet de re-router par drag & drop
  // les offres mal associées sans toucher au code de matching côté moteur.
  const [matchMode, setMatchMode] = useState(false);
  // Override par DQE. null = pas encore d'override (on lit adjustedLines + raw orphans).
  const [matchStateByKey, setMatchStateByKey] = useState<
    Record<string, { lines: RefLine[]; orphans: OrphanLine[] }>
  >({});
  type DragSource =
    | { kind: 'offer'; entName: string; sourceLineId: string }
    | { kind: 'orphan'; orphanId: string };
  type DropPosition = 'before' | 'on' | 'after';
  const [dragging, setDragging] = useState<DragSource | null>(null);
  const [dropTargetLineId, setDropTargetLineId] = useState<string | null>(null);
  const [dropTargetPosition, setDropTargetPosition] = useState<DropPosition>('on');

  const isOptional = (id: string): boolean => optionalIds.has(id);
  const toggleOptional = (id: string): void => {
    setOptionalIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const isRecap: boolean = activeLotId === RECAP_ID;
  const currentLot: Lot = MOCK_LOTS.find(l => l.id === activeLotId) ?? MOCK_LOTS[0];
  const sheets: BpuSheet[] = currentLot.sheets ?? [];
  const activeSheet: BpuSheet | null = sheets.length > 0 && activeSheetId !== null
    ? (sheets.find(s => s.id === activeSheetId) ?? null)
    : null;
  const isRawBpu: boolean = currentLot.type === 'BPU' && !activeSheet;
  const lotEnterprises = currentLot.enterprises;
  const scoreKeyForMemo: string = activeSheet ? `${activeLotId}:${activeSheet.id}` : activeLotId;
  const rawBuild = useMemo(() => buildViewLines(currentLot, activeSheet), [currentLot, activeSheet]);
  const rawLines: RefLine[] = rawBuild.lines;
  const rawOrphans: OrphanLine[] = rawBuild.orphans;
  // Étape 1 : applique les ajustements qty/PU stockés.
  const adjustedLines: RefLine[] = useMemo(() => {
    const adj = adjustByKey[scoreKeyForMemo];
    if (!adj || Object.keys(adj).length === 0) return rawLines;
    return rawLines.map(line => {
      if (line.isSection) return line;
      const lineAdj = adj[line.id];
      if (!lineAdj) return line;
      const newOffers: Record<string, Offer | null> = {};
      for (const name of Object.keys(line.offers)) {
        const offer = line.offers[name];
        if (!offer) { newOffers[name] = null; continue; }
        const a = lineAdj[name];
        if (!a || (a.qty === undefined && a.pu === undefined)) { newOffers[name] = offer; continue; }
        const qty = a.qty ?? offer.qty;
        const pu = a.pu ?? offer.pu;
        newOffers[name] = { ...offer, qty, pu, total: qty * pu };
      }
      return { ...line, offers: newOffers };
    });
  }, [rawLines, adjustByKey, scoreKeyForMemo]);
  // Étape 2 : si l'utilisateur a corrigé le matching (drag & drop), cette version gagne.
  // Sinon on retombe sur adjustedLines / rawOrphans.
  const matchOverride = matchStateByKey[scoreKeyForMemo];
  const lotLines: RefLine[] = matchOverride?.lines ?? adjustedLines;
  const lotOrphans: OrphanLine[] = matchOverride?.orphans ?? rawOrphans;
  const hasMatchOverride: boolean = !!matchOverride;
  // Offres originales (avant ajustements) — pour afficher la valeur d'origine au survol.
  const originalOffersByLine = useMemo(() => {
    const m = new Map<string, Record<string, Offer | null>>();
    for (const l of rawLines) {
      if (!l.isSection) m.set(l.id, l.offers);
    }
    return m;
  }, [rawLines]);

  const handleLotChange = (lotId: string): void => {
    if (lotId === RECAP_ID) {
      setActiveLotId(RECAP_ID);
      setPanelOpen(false);
      setFilter('all');
      setSearchTerm('');
      return;
    }
    const lot = MOCK_LOTS.find(l => l.id === lotId);
    if (!lot) return;
    setActiveLotId(lotId);
    setActiveSheetId(lot.sheets && lot.sheets.length > 0 ? lot.sheets[0].id : null);
    setPanelOpen(false);
    setFilter('all');
    setCollapsed({});
    setActiveEntTab(0);
    setSearchTerm('');
  };

  // Clé composite : chaque DQE d'un BPU a sa propre note/formule ; DPGF = lot unique.
  const scoreKey: string = activeSheet ? `${activeLotId}:${activeSheet.id}` : activeLotId;
  const lotScale: number = scaleByLot[scoreKey] ?? 60;
  const lotFormula: PriceFormula = formulaByLot[scoreKey] ?? 'inverse';
  const enterpriseStats = useMemo(
    () => computeEnterpriseStats(lotEnterprises, lotLines, optionalIds, undefined, undefined, lotScale, lotFormula),
    [lotEnterprises, lotLines, optionalIds, lotScale, lotFormula],
  );

  const updateScale = (v: number): void => {
    const clamped = Math.max(1, Math.min(1000, Math.round(v)));
    setScaleByLot(prev => ({ ...prev, [scoreKey]: clamped }));
  };

  const updateFormula = (f: PriceFormula): void => {
    setFormulaByLot(prev => ({ ...prev, [scoreKey]: f }));
  };
  const lotTopN: number = topNByLot[scoreKey] ?? 10;
  const updateTopN = (v: number): void => {
    const clamped = Math.max(1, Math.min(50, Math.round(v)));
    setTopNByLot(prev => ({ ...prev, [scoreKey]: clamped }));
  };

  // --- Ajustements ---
  const lineAdjustMap: Record<string, Record<string, { qty?: number; pu?: number }>> =
    adjustByKey[scoreKey] ?? {};
  const getAdj = (lineId: string, entName: string): { qty?: number; pu?: number } =>
    lineAdjustMap[lineId]?.[entName] ?? {};
  const updateAdjust = (lineId: string, entName: string, patch: { qty?: number; pu?: number }): void => {
    setAdjustByKey(prev => {
      const forKey = { ...(prev[scoreKey] ?? {}) };
      const forLine = { ...(forKey[lineId] ?? {}) };
      const cur = forLine[entName] ?? {};
      const next = { ...cur, ...patch };
      if (next.qty === undefined && next.pu === undefined) {
        delete forLine[entName];
      } else {
        forLine[entName] = next;
      }
      if (Object.keys(forLine).length === 0) delete forKey[lineId];
      else forKey[lineId] = forLine;
      return { ...prev, [scoreKey]: forKey };
    });
  };
  const setBulkQty = (line: RefPrestation, qty: number): void => {
    setAdjustByKey(prev => {
      const forKey = { ...(prev[scoreKey] ?? {}) };
      const forLine = { ...(forKey[line.id] ?? {}) };
      for (const name of Object.keys(line.offers)) {
        if (line.offers[name]) {
          const cur = forLine[name] ?? {};
          forLine[name] = { ...cur, qty };
        }
      }
      forKey[line.id] = forLine;
      return { ...prev, [scoreKey]: forKey };
    });
  };
  const clearAdjustValue = (lineId: string, entName: string, field: 'qty' | 'pu'): void => {
    setAdjustByKey(prev => {
      const forKey = { ...(prev[scoreKey] ?? {}) };
      const forLine = { ...(forKey[lineId] ?? {}) };
      const cur = { ...(forLine[entName] ?? {}) };
      delete cur[field];
      if (cur.qty === undefined && cur.pu === undefined) {
        delete forLine[entName];
      } else {
        forLine[entName] = cur;
      }
      if (Object.keys(forLine).length === 0) delete forKey[lineId];
      else forKey[lineId] = forLine;
      return { ...prev, [scoreKey]: forKey };
    });
  };
  const resetLineAdjust = (lineId: string): void => {
    setAdjustByKey(prev => {
      const forKey = { ...(prev[scoreKey] ?? {}) };
      delete forKey[lineId];
      return { ...prev, [scoreKey]: forKey };
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const resetAllAdjusts = (): void => {
    setAdjustByKey(prev => {
      const next = { ...prev };
      delete next[scoreKey];
      return next;
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const adjustCount: number = Object.values(lineAdjustMap).reduce(
    (acc, ent) => acc + Object.keys(ent).length,
    0,
  );

  // --- Matching (drag & drop) ---
  // Convertit un OrphanLine en Offer candidat pour hosting sur une RefPrestation.
  const orphanToOffer = (o: OrphanLine, host: RefPrestation): Offer => {
    const recalc = o.qty * o.pu;
    const refTotal = host.refQty * host.refPU;
    const ecart = refTotal > 0 ? ((recalc - refTotal) / refTotal) * 100 : 0;
    return {
      designation: o.designation,
      qty: o.qty,
      unit: o.unit,
      pu: o.pu,
      total: recalc,
      status: 'matched',
      ecart,
    };
  };
  // Convertit un Offer déplacé en OrphanLine. `home` = ligne réf de départ ; on mémorise son id
  // via `suggestion.refId` pour que l'orphelin réapparaisse inline au n+1 de sa ligne source.
  const offerToOrphan = (offer: Offer, entName: string, home: RefPrestation): OrphanLine => ({
    id: `orphan-${entName}-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
    enterprise: entName,
    designation: offer.designation,
    qty: offer.qty,
    unit: offer.unit ?? '',
    pu: offer.pu,
    total: offer.total,
    suggestion: { refId: home.id, refDesignation: home.designation, confidence: 1 },
  });
  // Recompute ecart when an offer lands on a different host line.
  const recomputeOffer = (offer: Offer, host: RefPrestation): Offer => {
    const recalc = offer.qty * offer.pu;
    const refTotal = host.refQty * host.refPU;
    const ecart = refTotal > 0 ? ((recalc - refTotal) / refTotal) * 100 : 0;
    return { ...offer, total: recalc, ecart };
  };

  // Retourne une copie profonde du state courant (override si présent, sinon adjusted/raw).
  const cloneMatchBase = (): { lines: RefLine[]; orphans: OrphanLine[] } => {
    if (matchOverride) {
      return {
        lines: structuredClone(matchOverride.lines),
        orphans: structuredClone(matchOverride.orphans),
      };
    }
    return {
      lines: structuredClone(adjustedLines),
      orphans: structuredClone(rawOrphans),
    };
  };

  // Construit une nouvelle ligne réf vide (offers = null pour toutes les ents) insérée
  // lors d'un drop entre deux lignes. Les champs réf sont tirés de l'offre déposée pour avoir
  // un libellé qui a du sens ; l'art est un placeholder qui rend explicite l'ajout manuel.
  const MATCH_INSERTED_PREFIX = 'match-ins-';
  const buildInsertedRefLine = (
    lotEnts: Enterprise[],
    donor: { designation: string; qty: number; unit: string; pu: number; level: number },
  ): RefPrestation => {
    const offers: Record<string, Offer | null> = {};
    for (const e of lotEnts) offers[e.name] = null;
    return {
      id: `${MATCH_INSERTED_PREFIX}${Date.now()}-${Math.round(Math.random() * 1e6)}`,
      art: '+ ajout',
      designation: donor.designation,
      level: donor.level,
      refQty: donor.qty,
      refUnit: donor.unit,
      refPU: donor.pu,
      offers,
    };
  };

  // Une ligne insérée qui n'a plus aucune offre n'a plus de raison d'exister.
  const pruneEmptyInserted = (lines: RefLine[]): RefLine[] =>
    lines.filter(l => {
      if (l.isSection) return true;
      if (!l.id.startsWith(MATCH_INSERTED_PREFIX)) return true;
      return Object.values(l.offers).some(o => o !== null);
    });

  // Applique un move. L'offre éventuellement présente sur la ligne de destination est renvoyée aux orphelins.
  const applyMatchMove = (
    source: DragSource,
    dest:
      | { kind: 'line'; lineId: string; position: DropPosition }
      | { kind: 'orphan' },
  ): void => {
    // Normaliser "drop sur soi-même" (position 'on' sur la source) = no-op.
    if (
      source.kind === 'offer'
      && dest.kind === 'line'
      && dest.position === 'on'
      && source.sourceLineId === dest.lineId
    ) return;
    const base = cloneMatchBase();

    // Helper : extraire l'offre à déplacer depuis la source.
    let movingDesignation = '';
    let movingUnit = '';
    let movingQty = 0;
    let movingPu = 0;
    let movingEnt = '';
    let movingOffer: Offer | null = null;
    let sourceLineLevel = 0;

    if (source.kind === 'offer') {
      const srcLine = base.lines.find(l => !l.isSection && l.id === source.sourceLineId);
      if (!srcLine || srcLine.isSection) return;
      const prestation = srcLine as RefPrestation;
      sourceLineLevel = prestation.level;
      const off = prestation.offers[source.entName];
      if (!off) return;
      movingOffer = off;
      movingDesignation = off.designation;
      movingUnit = off.unit ?? '';
      movingQty = off.qty;
      movingPu = off.pu;
      movingEnt = source.entName;
      prestation.offers[source.entName] = null;
    } else {
      const idx = base.orphans.findIndex(o => o.id === source.orphanId);
      if (idx === -1) return;
      const orph = base.orphans[idx];
      movingDesignation = orph.designation;
      movingUnit = orph.unit;
      movingQty = orph.qty;
      movingPu = orph.pu;
      movingEnt = orph.enterprise;
      // Ne retirer des orphelins qu'une fois la cible validée.
      if (dest.kind === 'orphan') return; // no-op
      base.orphans.splice(idx, 1);
    }

    if (dest.kind === 'orphan') {
      // Uniquement valide pour source=offer (orphan→orphan = no-op filtré ci-dessus).
      if (movingOffer && source.kind === 'offer') {
        const srcLine = base.lines.find(l => !l.isSection && l.id === source.sourceLineId) as RefPrestation | undefined;
        if (srcLine) base.orphans.push(offerToOrphan(movingOffer, movingEnt, srcLine));
      }
      base.lines = pruneEmptyInserted(base.lines);
      setMatchStateByKey(prev => ({ ...prev, [scoreKey]: base }));
      return;
    }

    // dest.kind === 'line'
    const destIdx = base.lines.findIndex(l => !l.isSection && l.id === dest.lineId);
    if (destIdx === -1) {
      // Cible invalide — restaurer.
      if (source.kind === 'offer' && movingOffer) {
        const srcLine = base.lines.find(l => !l.isSection && l.id === source.sourceLineId);
        if (srcLine && !srcLine.isSection) (srcLine as RefPrestation).offers[source.entName] = movingOffer;
      }
      base.lines = pruneEmptyInserted(base.lines);
    setMatchStateByKey(prev => ({ ...prev, [scoreKey]: base }));
      return;
    }

    if (dest.position === 'on') {
      const host = base.lines[destIdx] as RefPrestation;
      const displaced = host.offers[movingEnt];
      if (displaced) {
        // L'offre évincée retourne en orphelin rattaché à sa ligne d'origine (ici, host).
        base.orphans.push(offerToOrphan(displaced, movingEnt, host));
      }
      if (source.kind === 'offer' && movingOffer) {
        host.offers[movingEnt] = recomputeOffer(movingOffer, host);
      } else {
        host.offers[movingEnt] = orphanToOffer(
          { id: '', enterprise: movingEnt, designation: movingDesignation, qty: movingQty, unit: movingUnit, pu: movingPu, total: movingQty * movingPu, suggestion: null },
          host,
        );
      }
    } else {
      // Insertion avant/après : nouvelle ligne réf entre deux existantes, avec l'offre déposée dessus.
      const neighborLevel = (base.lines[destIdx] as RefPrestation).level;
      const newLine = buildInsertedRefLine(lotEnterprises, {
        designation: movingDesignation,
        qty: movingQty,
        unit: movingUnit,
        pu: movingPu,
        level: source.kind === 'offer' ? Math.max(neighborLevel, sourceLineLevel) : neighborLevel,
      });
      if (source.kind === 'offer' && movingOffer) {
        newLine.offers[movingEnt] = recomputeOffer(movingOffer, newLine);
      } else {
        newLine.offers[movingEnt] = orphanToOffer(
          { id: '', enterprise: movingEnt, designation: movingDesignation, qty: movingQty, unit: movingUnit, pu: movingPu, total: movingQty * movingPu, suggestion: null },
          newLine,
        );
      }
      const insertAt = dest.position === 'before' ? destIdx : destIdx + 1;
      base.lines.splice(insertAt, 0, newLine);
    }

    base.lines = pruneEmptyInserted(base.lines);
    setMatchStateByKey(prev => ({ ...prev, [scoreKey]: base }));
  };

  const resetMatchOverride = (): void => {
    setMatchStateByKey(prev => {
      const next = { ...prev };
      delete next[scoreKey];
      return next;
    });
  };

  // Compte le nombre de lignes modifiées (ligne où au moins une offre diffère de l'état initial).
  const matchMoveCount: number = useMemo(() => {
    if (!matchOverride) return 0;
    let count = 0;
    const base = adjustedLines;
    const cur = matchOverride.lines;
    for (const l of cur) {
      if (l.isSection) continue;
      const orig = base.find(b => !b.isSection && b.id === l.id) as RefPrestation | undefined;
      if (!orig) continue;
      for (const name of Object.keys(l.offers)) {
        const a = l.offers[name];
        const b = orig.offers[name];
        if ((a?.designation ?? null) !== (b?.designation ?? null)) count++;
      }
    }
    return count;
  }, [matchOverride, adjustedLines]);
  const statsByName = useMemo(() => {
    const m = new Map<string, EnterpriseStats>();
    enterpriseStats.forEach(s => m.set(s.name, s));
    return m;
  }, [enterpriseStats]);
  // Sous-totaux par section : Σ (qty×PU) par entreprise, sur les descendants (prestations non optionnelles).
  // Marche linéaire avec pile de sections actives — chaque prestation contribue à toutes ses sections
  // ancêtres en position. Les lignes insérées via matching (art '+ ajout') sont ainsi comptées dans
  // la section qui les héberge, contrairement à un filtre par préfixe d'art.
  const sectionSubtotals = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    const stack: Array<{ id: string; level: number }> = [];
    for (const r of lotLines) {
      if (r.isSection) {
        while (stack.length > 0 && stack[stack.length - 1].level >= r.level) stack.pop();
        stack.push({ id: r.id, level: r.level });
        if (!m.has(r.id)) m.set(r.id, {});
        continue;
      }
      if (optionalIds.has(r.id)) continue;
      for (const sec of stack) {
        const byEnt = m.get(sec.id)!;
        for (const name of Object.keys(r.offers)) {
          const offer = r.offers[name];
          if (!offer) continue;
          byEnt[name] = (byEnt[name] ?? 0) + offer.qty * offer.pu;
        }
      }
    }
    return m;
  }, [lotLines, optionalIds]);
  // Top-N lineIds par offre (écart important) — re-calculé quand lignes / topN changent.
  const ecartFlagged = useMemo(
    () => computeEcartFlags(lotEnterprises, lotLines, optionalIds, lotTopN),
    [lotEnterprises, lotLines, optionalIds, lotTopN],
  );
  // Outliers inter-offres par ligne (PU s'écartant de la médiane de ≥20%).
  const outlierFlagged = useMemo(
    () => computeOutlierFlags(lotLines, optionalIds),
    [lotLines, optionalIds],
  );
  // OAB ents (calculé via stats) : on remonte juste les noms flagués.
  const oabEnts = useMemo(() => {
    const s = new Set<string>();
    enterpriseStats.forEach(st => { if (st.isOAB) s.add(st.name); });
    return s;
  }, [enterpriseStats]);
  // Helper : alertes pour une cellule (ent × ligne). Renvoie level + raisons.
  const getCellAlerts = (line: RefPrestation, entName: string, offer: Offer | null) =>
    computeCellAlerts(line, entName, offer, {
      oabEnts,
      ecartFlagged,
      outlierFlagged,
      isOptional: optionalIds.has(line.id),
    });
  // Ordre toujours : moins cher → plus cher (totalRecalcule ; fallback totalHT en vue BPU brute).
  const orderedEnterprises: Enterprise[] = useMemo(() => {
    const priceOf = (e: Enterprise): number => {
      const s = statsByName.get(e.name);
      return s && s.totalRecalcule > 0 ? s.totalRecalcule : e.totalHT;
    };
    return [...lotEnterprises].sort((a, b) => priceOf(a) - priceOf(b));
  }, [lotEnterprises, statsByName]);

  // Orphans avec suggestion → regroupés par refId pour affichage inline sous la ligne ref.
  const orphansByRefId: Map<string, OrphanLine[]> = useMemo(() => {
    const m = new Map<string, OrphanLine[]>();
    for (const o of lotOrphans) {
      if (!o.suggestion) continue;
      const arr = m.get(o.suggestion.refId) ?? [];
      arr.push(o);
      m.set(o.suggestion.refId, arr);
    }
    return m;
  }, [lotOrphans]);

  const DRAWER_W = 740;
  const isSmall = windowWidth < 900;
  const isOverlay = windowWidth < DRAWER_W + 560;
  const isDetailed = viewMode === 'detailed';

  const hiddenEnts: Set<string> = hiddenEntsByLot[activeLotId] ?? new Set<string>();
  const displayedEnterprises: Enterprise[] = orderedEnterprises.filter(e => !hiddenEnts.has(e.name));
  const clampedEntTab = Math.min(activeEntTab, Math.max(0, displayedEnterprises.length - 1));
  const visibleEnterprises: Enterprise[] =
    (isSmall && isDetailed && displayedEnterprises.length > 0)
      ? [displayedEnterprises[clampedEntTab]]
      : displayedEnterprises;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleEntHidden = (name: string): void => {
    setHiddenEntsByLot(prev => {
      const cur = new Set(prev[activeLotId] ?? []);
      if (cur.has(name)) cur.delete(name); else cur.add(name);
      return { ...prev, [activeLotId]: cur };
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showAllEnts = (): void => {
    setHiddenEntsByLot(prev => ({ ...prev, [activeLotId]: new Set<string>() }));
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const showTopThreeEnts = (): void => {
    const topNames = new Set(orderedEnterprises.slice(0, 3).map(e => e.name));
    const hide = new Set(orderedEnterprises.filter(e => !topNames.has(e.name)).map(e => e.name));
    setHiddenEntsByLot(prev => ({ ...prev, [activeLotId]: hide }));
  };

  const openEntPanel = (ent: Enterprise) => {
    setPanelContent({ type: 'enterprise', data: ent });
    setPanelOpen(true);
  };
  const openRowPanel = (row: RefLine) => {
    if (row.isSection) return;
    setPanelContent({ type: 'row', data: row });
    setPanelOpen(true);
  };
  const openOrphanPanel = (orphan: OrphanLine) => {
    setPanelContent({ type: 'orphan', data: orphan });
    setPanelOpen(true);
  };
  const closePanel = () => setPanelOpen(false);
  const toggleCollapse = (id: string) =>
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  // Niveau d'alerte d'une ligne : 'red' si au moins une cellule rouge, 'orange' si au moins une orange.
  const rowAlertLevel = (row: RefPrestation): AlertLevel | null => {
    let hasOrange = false;
    for (const ent of lotEnterprises) {
      const { level } = getCellAlerts(row, ent.name, row.offers[ent.name] ?? null);
      if (level === 'red') return 'red';
      if (level === 'orange') hasOrange = true;
    }
    return hasOrange ? 'orange' : null;
  };

  const getCount = (type: 'alerts' | 'unmatched' | 'suspect'): number => {
    if (type === 'alerts') {
      return lotLines.filter(r => !r.isSection && rowAlertLevel(r as RefPrestation) === 'red').length;
    }
    if (type === 'unmatched') {
      return (
        lotLines.filter(r => !r.isSection && Object.values(r.offers).some(o => o === null)).length +
        lotOrphans.length
      );
    }
    if (type === 'suspect') {
      return lotLines.filter(r => !r.isSection && rowAlertLevel(r as RefPrestation) === 'orange').length;
    }
    return 0;
  };

  const isRowVisible = (row: RefPrestation): boolean => {
    if (filter === 'alerts') return rowAlertLevel(row) === 'red';
    if (filter === 'unmatched') return Object.values(row.offers).some(o => o === null);
    if (filter === 'suspect') return rowAlertLevel(row) === 'orange';
    return true;
  };

  const isChildHidden = (row: RefLine): boolean => {
    const parts = row.art.split('.');
    for (let i = 1; i < parts.length; i++) {
      if (collapsed[parts.slice(0, i).join('.')]) return true;
    }
    return false;
  };

  // Recherche texte dans la désignation. Les sections parentes d'un résultat sont conservées.
  const searchNormalized = searchTerm.trim().toLowerCase();
  const searchActive = searchNormalized.length > 0;
  const searchSets = useMemo(() => {
    if (!searchActive) return null;
    const ids = new Set<string>();
    const sectionArts = new Set<string>();
    for (const r of lotLines) {
      if (r.isSection) continue;
      if (r.designation.toLowerCase().includes(searchNormalized)) {
        ids.add(r.id);
        const parts = r.art.split('.');
        for (let i = 1; i < parts.length; i++) sectionArts.add(parts.slice(0, i).join('.'));
      }
    }
    return { ids, sectionArts };
  }, [lotLines, searchNormalized, searchActive]);

  // Quand un filtre ou la recherche est actif, on n'affiche que les sections dont au moins
  // un enfant passe le filtre — évite les sections vides dans la vue filtrée.
  const filterActive = filter !== 'all' || searchActive;
  const passedChildIds = new Set<string>();
  const passedSectionArts = new Set<string>();
  if (filterActive) {
    for (const r of lotLines) {
      if (r.isSection) continue;
      if (searchActive && !searchSets!.ids.has(r.id)) continue;
      if (!isRowVisible(r)) continue;
      passedChildIds.add(r.id);
      const parts = r.art.split('.');
      for (let i = 1; i < parts.length; i++) passedSectionArts.add(parts.slice(0, i).join('.'));
    }
  }

  const visibleData: RefLine[] = lotLines.filter(r => {
    if (isChildHidden(r)) return false;
    if (r.isSection) return filterActive ? passedSectionArts.has(r.art) : true;
    return filterActive ? passedChildIds.has(r.id) : true;
  });
  const totalToResolve = getCount('alerts') + getCount('suspect');

  // Stacked sticky: header (0) → lot tabs → (banner) → (ent tabs) → (dqe tabs) → thead.
  const HEADER_H = 60;
  const LOT_TABS_H = 42;
  const BANNER_H = 40;
  const TABS_H = 34;
  const DQE_TABS_H = 36;
  const showBanner = totalToResolve > 0 && !matchBannerDismissed;
  const showEntTabs = isSmall && isDetailed;
  const showDqeTabs = currentLot.type === 'BPU' && sheets.length > 0;
  const lotTabsTop = HEADER_H;
  const bannerTop = HEADER_H + LOT_TABS_H;
  const entTabsTop = bannerTop + (showBanner ? BANNER_H : 0);
  const dqeTabsTop = entTabsTop + (showEntTabs ? TABS_H : 0);
  const headerStickyTop = dqeTabsTop + (showDqeTabs ? DQE_TABS_H : 0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filterButtons: Array<{ key: FilterKey; label: string; color?: string }> = [
    { key: 'all', label: 'Tout' },
    { key: 'alerts', label: `Irrégularités (${getCount('alerts')})`, color: '#DC2626' },
    { key: 'suspect', label: `Vigilance (${getCount('suspect')})`, color: '#D97706' },
    { key: 'unmatched', label: `Ligne manquante (${getCount('unmatched')})`, color: '#DC2626' },
  ];

  return (
    <div style={rootStyle}>
      {/* Header */}
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--panel-bg)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em' }}>Sillant</div>
          <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Résidence Les Ormes</div>
            {!isSmall && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{currentLot.name}</span>
                <span style={{ color: 'var(--border)' }}>·</span>
                <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600 }}>{currentLot.type}</span>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!isRecap && !isRawBpu && (
            <>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setScaleOpen(o => !o)}
                  title="Total de la note financière"
                  style={{
                    padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    border: '1px solid var(--border)',
                    background: scaleOpen ? 'var(--card-bg)' : 'transparent',
                    color: 'var(--text-primary)',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <ScaleIcon style={{ width: 13, height: 13, color: 'var(--text-secondary)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Note /</span>
                  <span style={{ fontWeight: 600 }}>{lotScale}</span>
                </button>
                {scaleOpen && (
                  <>
                    <div onClick={() => setScaleOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 210 }} />
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 220,
                      minWidth: 300, padding: 14, borderRadius: 10,
                      background: 'var(--panel-bg)', border: '1px solid var(--border)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Total de la note
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Noter sur</span>
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          step={1}
                          value={lotScale}
                          onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v)) updateScale(v);
                          }}
                          style={{
                            width: 90, padding: '5px 8px', borderRadius: 6,
                            border: '1px solid var(--border)', background: 'var(--bg)',
                            fontSize: 13, fontWeight: 600, textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)',
                            outline: 'none',
                          }}
                        />
                        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>points</span>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Formule de calcul
                      </div>
                      <select
                        value={lotFormula}
                        onChange={e => updateFormula(e.target.value as PriceFormula)}
                        style={{
                          width: '100%', padding: '7px 10px', borderRadius: 6,
                          border: '1px solid var(--border)', background: 'var(--bg)',
                          fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
                          outline: 'none', cursor: 'pointer',
                        }}
                      >
                        <option value="inverse">Proportionnelle inverse · N = Nmax × Pmin / P</option>
                        <option value="linear">Linéaire min-max · N = Nmax × (Pmax − P) / (Pmax − Pmin)</option>
                        <option value="subtractive">Soustractive · N = Nmax × (1 − (P − Pmin) / Pmin)</option>
                      </select>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, lineHeight: 1.45 }}>
                        {lotFormula === 'inverse' && (<>Standard « moins-disant ». Plus l'offre s'éloigne du mini, plus la note chute doucement (hyperbolique).</>)}
                        {lotFormula === 'linear' && (<>Étale les notes sur l'intervalle : meilleur = {lotScale}, pire = 0.</>)}
                        {lotFormula === 'subtractive' && (<>Pénalise linéairement l'écart au mini. Note nulle dès +100% du mini.</>)}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setAlertsOpen(o => !o)}
                  title="Écarts orange à afficher par offre"
                  style={{
                    padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    border: '1px solid var(--border)',
                    background: alertsOpen ? 'var(--card-bg)' : 'transparent',
                    color: 'var(--text-primary)',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <ExclamationTriangleIcon style={{ width: 13, height: 13, color: '#D97706' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Top</span>
                  <span style={{ fontWeight: 600 }}>{lotTopN}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>écarts</span>
                </button>
                {alertsOpen && (
                  <>
                    <div onClick={() => setAlertsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 210 }} />
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 220,
                      minWidth: 320, padding: 14, borderRadius: 10,
                      background: 'var(--panel-bg)', border: '1px solid var(--border)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Écarts orange par offre
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <input
                          type="range"
                          min={1}
                          max={30}
                          step={1}
                          value={lotTopN}
                          onChange={e => updateTopN(parseInt(e.target.value, 10))}
                          style={{ flex: 1, accentColor: '#D97706' }}
                        />
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={lotTopN}
                          onChange={e => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v)) updateTopN(v);
                          }}
                          style={{
                            width: 60, padding: '5px 8px', borderRadius: 6,
                            border: '1px solid var(--border)', background: 'var(--bg)',
                            fontSize: 13, fontWeight: 600, textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)',
                            outline: 'none',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45 }}>
                        Pour chaque offre, on met en évidence les <b style={{ color: 'var(--text-secondary)' }}>{lotTopN}</b> lignes aux écarts les plus importants (≥ 5%). Les irrégularités rouges s'affichent toujours.
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setMatchMode(m => !m)}
                title={matchMode ? 'Quitter le mode matching' : 'Corriger le rapprochement par drag & drop'}
                style={{
                  padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  border: `1px solid ${matchMode ? '#7C3AED' : 'var(--border)'}`,
                  background: matchMode ? '#7C3AED' : 'transparent',
                  color: matchMode ? '#fff' : 'var(--text-primary)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <ArrowsPointingOutIcon style={{ width: 13, height: 13 }} />
                <span>Matching</span>
                {hasMatchOverride && matchMoveCount > 0 && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                    background: matchMode ? 'rgba(255,255,255,0.22)' : '#7C3AED',
                    color: '#fff', letterSpacing: '0.05em',
                  }}>{matchMoveCount}</span>
                )}
              </button>
              {hasMatchOverride && (
                <button
                  onClick={resetMatchOverride}
                  title="Réinitialiser toutes les corrections de matching pour cette DQE"
                  style={{
                    padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'transparent',
                    color: 'var(--text-secondary)',
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <XMarkIcon style={{ width: 12, height: 12 }} />
                  Réinit. matching
                </button>
              )}
              <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px' }} />
            </>
          )}
          <button
            disabled
            title="Ajouter une entreprise (proto — action désactivée)"
            style={{
              padding: '7px 12px', borderRadius: 8, border: '1px dashed var(--border)',
              background: 'transparent', fontSize: 13, color: 'var(--text-secondary)',
              cursor: 'not-allowed', opacity: 0.6,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <UserPlusIcon style={{ width: 14, height: 14 }} />
            Ajouter une entreprise
          </button>
          <button style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Exporter</button>
          {/* PARKED: bouton Synthèse — voir PARKED_FEATURES.md §1 */}
        </div>
      </div>

      {/* Lot tabs */}
      <div style={{
        padding: '0 20px', background: 'var(--panel-bg)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 2, position: 'sticky', top: lotTabsTop, zIndex: 97,
        overflowX: 'auto',
      }}>
        {showRecapTab && (
          <button onClick={() => handleLotChange(RECAP_ID)} style={{
            padding: '11px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: isRecap ? 600 : 500,
            color: isRecap ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderBottom: isRecap ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1,
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, whiteSpace: 'nowrap',
          }}>
            <Squares2X2Icon style={{ width: 14, height: 14 }} />
            <span>Récapitulatif</span>
          </button>
        )}
        {MOCK_LOTS.map(lot => {
          const active = lot.id === activeLotId;
          return (
            <button key={lot.id} onClick={() => handleLotChange(lot.id)} style={{
              padding: '11px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: active ? 600 : 500,
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
              display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, whiteSpace: 'nowrap',
            }}>
              <span>{lot.name}</span>
              <span style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 10, padding: '1px 6px', borderRadius: 4,
                background: active ? 'var(--accent)15' : 'var(--card-bg)',
                color: active ? 'var(--accent)' : 'var(--text-secondary)',
                fontWeight: 700, letterSpacing: '0.05em',
              }}>{lot.type}</span>
            </button>
          );
        })}
        <button style={{
          padding: '6px 10px', background: 'transparent', border: '1px dashed var(--border)',
          cursor: 'pointer', borderRadius: 6, color: 'var(--text-secondary)', display: 'flex',
          alignItems: 'center', gap: 4, fontSize: 12, marginLeft: 8, flexShrink: 0,
        }}>
          <PlusIcon style={{ width: 12, height: 12 }} /> Lot
        </button>
      </div>

      {isRecap ? (() => {
        // Tous les répondants uniques (par nom), ordre 1ʳᵉ apparition.
        const recapEnts: Enterprise[] = [];
        const seen = new Set<string>();
        for (const lot of MOCK_LOTS) {
          for (const ent of lot.enterprises) {
            if (!seen.has(ent.name)) { seen.add(ent.name); recapEnts.push(ent); }
          }
        }
        // Pour chaque lot : total recalculé par entreprise + min.
        const rows = MOCK_LOTS.map(lot => {
          const sheet = lot.sheets && lot.sheets.length > 0 ? lot.sheets[0] : null;
          const view = buildViewLines(lot, sheet);
          const stats = computeEnterpriseStats(lot.enterprises, view.lines, new Set());
          const byName = new Map(stats.map(s => [s.name, s]));
          const totals: Record<string, number | null> = {};
          for (const ent of recapEnts) {
            const s = byName.get(ent.name);
            totals[ent.name] = s && s.totalRecalcule > 0 ? s.totalRecalcule : null;
          }
          const bidVals = Object.values(totals).filter((v): v is number => v !== null);
          const minTotal = bidVals.length > 0 ? Math.min(...bidVals) : null;
          return { lot, totals, minTotal };
        });
        // Total consultation = Σ moins-disants par lot.
        const totalConsult = rows.reduce((acc, r) => acc + (r.minTotal ?? 0), 0);
        // Total par entreprise = somme des lots où elle a répondu.
        const entTotals: Record<string, number> = {};
        for (const ent of recapEnts) {
          entTotals[ent.name] = rows.reduce((acc, r) => acc + (r.totals[ent.name] ?? 0), 0);
        }
        return (
          <div style={{ padding: '28px 32px' }}>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', marginBottom: 4 }}>Récapitulatif de la consultation</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
              {MOCK_LOTS.length} lots · {recapEnts.length} répondant{recapEnts.length > 1 ? 's' : ''} · {MOCK_LOTS.reduce((n, l) => n + l.enterprises.length, 0)} offres reçues
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto', background: 'var(--panel-bg)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--card-bg)', borderBottom: '2px solid var(--border)' }}>
                    <th style={{ ...thBase, textAlign: 'left', paddingLeft: 16, minWidth: 200 }}>Lot</th>
                    <th style={{ ...thBase, width: 70 }}>Type</th>
                    {recapEnts.map(ent => (
                      <th key={ent.name} style={{ ...thBase, minWidth: 110, textAlign: 'right', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', borderLeft: `2px solid ${ent.color}30`, background: `${ent.color}08` }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: ent.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{ent.name}</span>
                        </div>
                      </th>
                    ))}
                    <th style={{ ...thBase, width: 150, textAlign: 'right', paddingRight: 16, borderLeft: '2px solid var(--border)' }}>Moins-disant</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ lot, totals, minTotal }) => {
                    const bestEntName = minTotal !== null ? recapEnts.find(e => totals[e.name] === minTotal)?.name : null;
                    const bestEnt = bestEntName ? recapEnts.find(e => e.name === bestEntName) : null;
                    return (
                      <tr
                        key={lot.id}
                        onClick={() => handleLotChange(lot.id)}
                        style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--card-bg)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ ...tdBase, paddingLeft: 16, fontWeight: 500 }}>{lot.name}</td>
                        <td style={{ ...tdBase, textAlign: 'center' }}>
                          <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--card-bg)', color: 'var(--text-secondary)', fontWeight: 700, letterSpacing: '0.05em' }}>{lot.type}</span>
                        </td>
                        {recapEnts.map(ent => {
                          const val = totals[ent.name];
                          const isBest = val !== null && val === minTotal;
                          return (
                            <td key={ent.name} style={{ ...tdBase, textAlign: 'right', padding: '8px 12px', borderLeft: `2px solid ${ent.color}20`, fontVariantNumeric: 'tabular-nums', fontWeight: isBest ? 700 : 500, color: val === null ? 'var(--muted)' : (isBest ? '#059669' : 'var(--text-primary)') }}>
                              {val === null ? '—' : fmt(val)}
                            </td>
                          );
                        })}
                        <td style={{ ...tdBase, textAlign: 'right', paddingRight: 16, borderLeft: '2px solid var(--border)', fontVariantNumeric: 'tabular-nums' }}>
                          {bestEnt && minTotal !== null ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: bestEnt.color, flexShrink: 0 }} />
                              <span style={{ fontWeight: 700, color: '#059669' }}>{fmt(minTotal)}</span>
                            </div>
                          ) : <span style={{ color: 'var(--muted)' }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--card-bg)', borderTop: '2px solid var(--text-primary)' }}>
                    <td colSpan={2} style={{ padding: '14px 16px', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Total répondant
                    </td>
                    {recapEnts.map(ent => (
                      <td key={ent.name} style={{ padding: '14px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700, borderLeft: `2px solid ${ent.color}30`, background: `${ent.color}08` }}>
                        {entTotals[ent.name] > 0 ? fmt(entTotals[ent.name]) : <span style={{ color: 'var(--muted)', fontWeight: 400 }}>—</span>}
                      </td>
                    ))}
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 700, borderLeft: '2px solid var(--border)', color: '#059669' }}>
                      {fmt(totalConsult)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10 }}>
              Cliquez sur un lot pour ouvrir le détail · Les montants en vert indiquent le moins-disant par lot.
            </div>
          </div>
        );
      })() : (<>
      {/* PARKED: bandeau "X éléments à vérifier" — voir PARKED_FEATURES.md §2 */}
      {/* PARKED: toolbar complète (filtres, toggle, Ajuster, bulles entreprise) — voir PARKED_FEATURES.md §3/§4/§5/§6/§9 */}
      {/* NOTE: dropdowns Note/scale + Top N écarts migrés dans le header (voir §9) */}
      {/* PARKED: barre d'onglets entreprise (mobile/détaillée) — voir PARKED_FEATURES.md §8 */}

      {/* Vue BPU / DQE sub-tabs (BPU only) */}
      {showDqeTabs && (
        <div style={{
          padding: '7px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: dqeTabsTop, zIndex: 80,
        }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)',
            letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>Vue</span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button
              onClick={() => { setActiveSheetId(null); setPanelOpen(false); }}
              title="Vue BPU — catalogue brut, pour constater les postes non chiffrés et les lignes manquantes"
              style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                border: !activeSheet ? '1px solid var(--text-primary)' : '1px solid var(--border)',
                background: !activeSheet ? 'var(--text-primary)' : 'transparent',
                color: !activeSheet ? 'var(--bg)' : 'var(--text-secondary)',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <DocumentTextIcon style={{ width: 12, height: 12 }} />
              BPU
            </button>
            <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
            {sheets.map(s => {
              const active = activeSheet?.id === s.id;
              return (
                <button key={s.id} onClick={() => { setActiveSheetId(s.id); setPanelOpen(false); }} style={{
                  padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                  background: active ? 'var(--accent)' : 'transparent',
                  color: active ? '#fff' : 'var(--text-secondary)',
                }}>
                  {s.name}
                </button>
              );
            })}
            <button style={{
              padding: '5px 8px', borderRadius: 6, border: '1px dashed var(--border)',
              background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex',
            }}>
              <PlusIcon style={{ width: 12, height: 12 }} />
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <div style={{ display: 'flex', position: 'relative' }}>
        <div style={{ flex: 1, transition: 'margin-right 0.25s ease', marginRight: panelOpen && !isOverlay ? DRAWER_W : 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: isDetailed ? '1px solid var(--border)' : '2px solid var(--border)', position: 'sticky', top: headerStickyTop, background: 'var(--bg)', zIndex: 10 }}>
                <th rowSpan={isDetailed ? 2 : 1} style={{ ...thBase, width: REF_W.art, textAlign: 'left', paddingLeft: 12, position: 'sticky', left: REF_L.art, background: 'var(--bg)', zIndex: 11 }}>Art.</th>
                <th rowSpan={isDetailed ? 2 : 1} style={{ ...thBase, width: REF_W.desig, textAlign: 'left', position: 'sticky', left: REF_L.desig, background: 'var(--bg)', zIndex: 11, padding: '6px 8px', textTransform: 'none', letterSpacing: 'normal' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%' }}>
                    <MagnifyingGlassIcon style={{ width: 12, height: 12, color: searchActive ? '#7C3AED' : 'var(--muted)', flexShrink: 0 }} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      placeholder="Désignation"
                      style={{
                        flex: 1, minWidth: 0,
                        border: '1px solid ' + (searchActive ? '#7C3AED' : 'transparent'),
                        borderRadius: 4, padding: '2px 5px',
                        background: searchActive ? '#7C3AED08' : 'transparent',
                        outline: 'none', fontSize: 11, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        color: searchActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                      }}
                    />
                    {searchActive && (
                      <button
                        onClick={() => setSearchTerm('')}
                        title="Effacer la recherche"
                        style={{ padding: 0, width: 16, height: 16, border: 'none', borderRadius: 3, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                      >
                        <XMarkIcon style={{ width: 11, height: 11 }} />
                      </button>
                    )}
                  </div>
                </th>
                <th rowSpan={isDetailed ? 2 : 1} style={{ ...thBase, width: REF_W.qty, position: 'sticky', left: REF_L.qty, background: 'var(--bg)', zIndex: 11 }}>Qté</th>
                <th rowSpan={isDetailed ? 2 : 1} style={{ ...thBase, width: REF_W.unit, position: 'sticky', left: REF_L.unit, background: 'var(--bg)', zIndex: 11 }}>U</th>
                <th rowSpan={isDetailed ? 2 : 1} style={{ ...thBase, width: REF_W.pu, position: 'sticky', left: REF_L.pu, background: 'var(--bg)', zIndex: 11, boxShadow: '1px 0 0 0 var(--border) inset' }}>PU Réf</th>
                <th rowSpan={isDetailed ? 2 : 1} style={{ ...thBase, width: REF_W.total, position: 'sticky', left: REF_L.total, background: 'var(--bg)', zIndex: 11, boxShadow: '2px 0 0 -1px var(--border)' }}>Total Réf</th>
                {visibleEnterprises.map(ent => (
                  isDetailed ? (
                    <th
                      key={ent.name}
                      colSpan={4}
                      onClick={() => openEntPanel(ent)}
                      title={`Ouvrir le détail de ${ent.name}`}
                      style={{
                        ...thBase, borderLeft: `2px solid ${ent.color}30`, background: `${ent.color}08`,
                        padding: '7px 8px', textTransform: 'none', letterSpacing: 'normal', cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: ent.color, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{ent.name}</span>
                      </div>
                    </th>
                  ) : (
                    <th
                      key={ent.name}
                      colSpan={2}
                      onClick={() => openEntPanel(ent)}
                      title={`Ouvrir le détail de ${ent.name}`}
                      style={{ ...thBase, borderLeft: `2px solid ${ent.color}30`, minWidth: 160, cursor: 'pointer', background: `${ent.color}08` }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: ent.color }} />{ent.name}
                      </div>
                    </th>
                  )
                ))}
              </tr>
              {isDetailed && (
                <tr style={{ borderBottom: '2px solid var(--border)', position: 'sticky', top: headerStickyTop + 34, background: 'var(--bg)', zIndex: 10 }}>
                  {visibleEnterprises.map(ent => (
                    <React.Fragment key={ent.name}>
                      <th style={{ ...thBase, borderLeft: `2px solid ${ent.color}30`, width: 40, background: 'var(--bg)' }}>Qté</th>
                      <th style={{ ...thBase, width: 56, background: 'var(--bg)' }}>PU</th>
                      <th style={{ ...thBase, width: 70, background: 'var(--bg)' }}>Total</th>
                      <th style={{ ...thBase, width: 68, background: 'var(--bg)' }} />
                    </React.Fragment>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {visibleData.map(row => {
                if (row.isSection) {
                  const isLvl0 = row.level === 0;
                  const sectionBg = isLvl0 ? 'var(--card-bg)' : 'var(--bg)';
                  const subs = sectionSubtotals.get(row.id) ?? {};
                  const entVals = visibleEnterprises.map(e => subs[e.name] ?? 0).filter(v => v > 0);
                  const sectionMin = entVals.length > 0 ? Math.min(...entVals) : 0;
                  // Alerte d'écart sur sous-total : delta vs min ≥ seuil (aligné sur prestations).
                  const SUB_ECART_MIN_PCT = 5;
                  const subDeltaPct = (entName: string): number | null => {
                    const val = subs[entName] ?? 0;
                    if (val <= 0 || sectionMin <= 0 || val === sectionMin) return null;
                    return ((val - sectionMin) / sectionMin) * 100;
                  };
                  const renderSubAmount = (entName: string): React.ReactNode => {
                    const val = subs[entName] ?? 0;
                    if (val <= 0) return null;
                    const delta = subDeltaPct(entName);
                    const flagged = delta != null && delta >= SUB_ECART_MIN_PCT;
                    const isMin = delta == null && val > 0;
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                        {flagged && <AlertDot level="orange" />}
                        <span style={{ color: isMin ? '#059669' : 'var(--text-secondary)', fontWeight: isLvl0 ? 700 : 600, fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>{fmtCompact(val)}</span>
                      </span>
                    );
                  };
                  const subTip = (entName: string): string | undefined => {
                    const delta = subDeltaPct(entName);
                    if (delta == null) return undefined;
                    if (delta < SUB_ECART_MIN_PCT) return undefined;
                    return `Sous-total ${fmt(subs[entName] ?? 0)} · +${delta.toFixed(1)}% vs moins-disant (${fmt(sectionMin)})`;
                  };
                  return (
                    <tr key={row.id} onClick={() => toggleCollapse(row.art)} style={{ cursor: 'pointer', background: sectionBg, borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...tdBase, fontWeight: 600, color: 'var(--text-secondary)', fontSize: 12, paddingLeft: 12, width: REF_W.art, position: 'sticky', left: REF_L.art, background: sectionBg, zIndex: 4 }}>{row.art}</td>
                      <td colSpan={5} style={{ ...tdBase, fontWeight: isLvl0 ? 700 : 600, fontSize: 13, paddingLeft: 12 + row.level * 16, position: 'sticky', left: REF_L.desig, background: sectionBg, zIndex: 4, boxShadow: '2px 0 0 -1px var(--border)' }}>
                        {collapsed[row.art]
                          ? <ChevronRightIcon style={{ width: 12, height: 12, color: 'var(--muted)', display: 'inline-block', verticalAlign: '-1px', marginRight: 6 }} />
                          : <ChevronDownIcon style={{ width: 12, height: 12, color: 'var(--muted)', display: 'inline-block', verticalAlign: '-1px', marginRight: 6 }} />
                        }
                        {row.designation}
                      </td>
                      {visibleEnterprises.map(ent => {
                        const delta = subDeltaPct(ent.name);
                        const ecartNode = delta != null ? (
                          <span style={{ fontSize: 10, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>+{delta.toFixed(0)}%</span>
                        ) : null;
                        return isDetailed ? (
                          <React.Fragment key={ent.name}>
                            <td style={{ ...tdBase, background: sectionBg, borderLeft: `2px solid ${ent.color}30` }} />
                            <td style={{ ...tdBase, background: sectionBg }} />
                            <td style={{ ...tdBase, background: sectionBg, textAlign: 'right' }} title={subTip(ent.name)}>{renderSubAmount(ent.name)}</td>
                            <td style={{ ...tdBase, background: sectionBg, textAlign: 'right' }} title={subTip(ent.name)}>{ecartNode}</td>
                          </React.Fragment>
                        ) : (
                          <React.Fragment key={ent.name}>
                            <td style={{ ...tdBase, background: sectionBg, borderLeft: `2px solid ${ent.color}30`, textAlign: 'right' }} title={subTip(ent.name)}>{renderSubAmount(ent.name)}</td>
                            <td style={{ ...tdBase, background: sectionBg, textAlign: 'right' }} title={subTip(ent.name)}>{ecartNode}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                }
                const isSelected = panelContent?.type === 'row' && panelContent?.data?.id === row.id;
                const rowOptional = isOptional(row.id);
                // Agrège alertes de la ligne sur toutes les offres visibles.
                const cellAlertsByEnt = new Map<string, ReturnType<typeof getCellAlerts>>();
                visibleEnterprises.forEach(ent => {
                  cellAlertsByEnt.set(ent.name, getCellAlerts(row, ent.name, row.offers[ent.name] ?? null));
                });
                const rowHasRed = Array.from(cellAlertsByEnt.values()).some(a => a.level === 'red');
                const rowHasOrange = !rowHasRed && Array.from(cellAlertsByEnt.values()).some(a => a.level === 'orange');
                const rowAccent = rowHasRed ? '#DC2626' : rowHasOrange ? '#D97706' : null;
                const lineHasAdjust = !!lineAdjustMap[row.id];
                const defaultBg = isSelected ? '#7C3AED08' : lineHasAdjust ? '#7C3AED06' : 'transparent';
                const inlineOrphans = orphansByRefId.get(row.id) ?? [];
                // Meilleur / pire PU parmi les offres visibles — indicateur directionnel ▼/▲.
                let bestPuEnt: string | null = null;
                let worstPuEnt: string | null = null;
                const entPUs: Array<{ name: string; pu: number }> = [];
                visibleEnterprises.forEach(ent => {
                  const off = row.offers[ent.name];
                  if (off && off.pu > 0) entPUs.push({ name: ent.name, pu: off.pu });
                });
                if (entPUs.length >= 2) {
                  const minPu = Math.min(...entPUs.map(x => x.pu));
                  const maxPu = Math.max(...entPUs.map(x => x.pu));
                  if (minPu !== maxPu) {
                    bestPuEnt = entPUs.find(x => x.pu === minPu)?.name ?? null;
                    worstPuEnt = entPUs.find(x => x.pu === maxPu)?.name ?? null;
                  }
                }
                return (
                  <React.Fragment key={row.id}>
                  <tr
                    onClick={() => { if (!matchMode) openRowPanel(row); }}
                    onDragOver={matchMode ? (e) => {
                      if (!dragging) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      const rect = e.currentTarget.getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const edge = Math.min(8, rect.height / 4);
                      let pos: DropPosition;
                      if (y < edge) pos = 'before';
                      else if (y > rect.height - edge) pos = 'after';
                      else pos = 'on';
                      // Drop 'on' la ligne source = no-op ; on bascule vers 'before' pour rester réactif.
                      if (pos === 'on' && dragging.kind === 'offer' && dragging.sourceLineId === row.id) {
                        pos = y < rect.height / 2 ? 'before' : 'after';
                      }
                      if (dropTargetLineId !== row.id) setDropTargetLineId(row.id);
                      if (dropTargetPosition !== pos) setDropTargetPosition(pos);
                    } : undefined}
                    onDragLeave={matchMode ? () => {
                      if (dropTargetLineId === row.id) setDropTargetLineId(null);
                    } : undefined}
                    onDrop={matchMode ? (e) => {
                      if (!dragging) return;
                      e.preventDefault();
                      applyMatchMove(dragging, { kind: 'line', lineId: row.id, position: dropTargetPosition });
                      setDragging(null);
                      setDropTargetLineId(null);
                      setDropTargetPosition('on');
                    } : undefined}
                    style={{
                      ['--row-bg' as unknown as string]: (matchMode && dropTargetLineId === row.id && dropTargetPosition === 'on') ? '#7C3AED1A' : defaultBg,
                      cursor: matchMode ? 'default' : 'pointer',
                      borderTop: (matchMode && dropTargetLineId === row.id && dropTargetPosition === 'before') ? '3px solid #7C3AED' : undefined,
                      borderBottom: (matchMode && dropTargetLineId === row.id && dropTargetPosition === 'after') ? '3px solid #7C3AED' : '1px solid var(--border)',
                      background: (matchMode && dropTargetLineId === row.id && dropTargetPosition === 'on') ? '#7C3AED1A' : defaultBg,
                      borderLeft: (matchMode && dropTargetLineId === row.id && dropTargetPosition === 'on')
                        ? '3px solid #7C3AED'
                        : (rowAccent ? `3px solid ${rowAccent}` : '3px solid transparent'),
                      opacity: rowOptional ? 0.5 : 1,
                      fontStyle: rowOptional ? 'italic' : 'normal',
                    } as React.CSSProperties}
                    onMouseEnter={e => {
                      if (matchMode) return;
                      if (!isSelected) {
                        e.currentTarget.style.background = '#F3F4F6';
                        e.currentTarget.style.setProperty('--row-bg', '#F3F4F6');
                      }
                    }}
                    onMouseLeave={e => {
                      if (matchMode) return;
                      e.currentTarget.style.background = defaultBg;
                      e.currentTarget.style.setProperty('--row-bg', defaultBg);
                    }}
                  >
                    <td style={{ ...tdBase, color: 'var(--text-secondary)', fontSize: 12, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", paddingLeft: 10, width: REF_W.art, position: 'sticky', left: REF_L.art, background: 'var(--row-bg)', zIndex: 3 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {lineHasAdjust && (
                          editMode ? (
                            <button
                              onClick={e => { e.stopPropagation(); resetLineAdjust(row.id); }}
                              title="Réinitialiser les ajustements de cette ligne"
                              style={{ padding: 0, width: 14, height: 14, border: 'none', borderRadius: 3, background: '#7C3AED', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                            >
                              <XMarkIcon style={{ width: 10, height: 10 }} />
                            </button>
                          ) : (
                            <span title="Ligne ajustée" aria-hidden style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#7C3AED', flexShrink: 0 }} />
                          )
                        )}
                        {row.art}
                      </span>
                    </td>
                    <td style={{ ...tdBase, paddingLeft: 8 + row.level * 12, fontWeight: 500, width: REF_W.desig, position: 'sticky', left: REF_L.desig, background: 'var(--row-bg)', zIndex: 3, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.designation}</td>
                    <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)', width: REF_W.qty, position: 'sticky', left: REF_L.qty, background: 'var(--row-bg)', zIndex: 3 }}>{isRawBpu ? <span style={{ color: 'var(--muted)' }}>—</span> : fmtNum(row.refQty)}</td>
                    <td style={{ ...tdBase, textAlign: 'center', color: 'var(--muted)', fontSize: 12, width: REF_W.unit, position: 'sticky', left: REF_L.unit, background: 'var(--row-bg)', zIndex: 3 }}>{row.refUnit}</td>
                    <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)', width: REF_W.pu, position: 'sticky', left: REF_L.pu, background: 'var(--row-bg)', zIndex: 3 }}>{fmt(row.refPU)}</td>
                    <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500, width: REF_W.total, position: 'sticky', left: REF_L.total, background: 'var(--row-bg)', zIndex: 3, boxShadow: '2px 0 0 -1px var(--border)' }}>{isRawBpu ? <span style={{ color: 'var(--muted)' }}>—</span> : fmt(row.refQty * row.refPU)}</td>
                    {visibleEnterprises.map(ent => {
                      const offer = row.offers[ent.name];
                      const cellAlerts = cellAlertsByEnt.get(ent.name);
                      const cellLevel = cellAlerts?.level ?? null;
                      const bgCell = cellLevel === 'red' ? '#DC262608' : cellLevel === 'orange' ? '#D9770608' : 'transparent';
                      const cellTooltip = cellAlerts && cellAlerts.reasons.length > 0
                        ? cellAlerts.reasons.map(r => r.message).join(' · ')
                        : undefined;
                      if (matchMode) {
                        const cols = isDetailed ? 4 : 2;
                        const isBeingDragged = dragging?.kind === 'offer' && dragging.entName === ent.name && dragging.sourceLineId === row.id;
                        const matchTip = offer
                          ? `${offer.designation}${cellTooltip ? ' · ' + cellTooltip : ''}`
                          : 'Ligne non matchée — déposez ici une offre d\'un autre lot';
                        return (
                          <td
                            key={ent.name}
                            colSpan={cols}
                            draggable={!!offer}
                            onDragStart={offer ? (e) => {
                              e.stopPropagation();
                              const src: DragSource = { kind: 'offer', entName: ent.name, sourceLineId: row.id };
                              setDragging(src);
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', `${ent.name}@${row.id}`);
                            } : undefined}
                            onDragEnd={() => { setDragging(null); setDropTargetLineId(null); }}
                            title={matchTip}
                            style={{
                              ...tdBase,
                              borderLeft: `2px solid ${ent.color}30`,
                              background: offer ? bgCell : '#DC262608',
                              cursor: offer ? 'grab' : 'default',
                              opacity: isBeingDragged ? 0.4 : 1,
                              padding: '6px 10px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                              {offer ? (
                                <>
                                  {cellLevel && <AlertDot level={cellLevel} />}
                                  <span style={{
                                    fontSize: 12, color: 'var(--text-primary)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    flex: 1, minWidth: 0,
                                  }}>{offer.designation}</span>
                                </>
                              ) : (
                                <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <AlertDot level="red" />
                                  manquante
                                </span>
                              )}
                            </div>
                          </td>
                        );
                      }
                      if (!offer) {
                        const cols = isDetailed ? 4 : 2;
                        return (
                          <React.Fragment key={ent.name}>
                            {Array.from({ length: cols }, (_, i) => (
                              <td
                                key={ent.name + '-e-' + i}
                                title={cellTooltip}
                                style={{ ...tdBase, textAlign: 'center', borderLeft: i === 0 ? `2px solid ${ent.color}30` : 'none', background: '#DC262608' }}
                              >
                                {i === 0 && <span style={{ fontSize: 11, color: '#DC2626', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                  <AlertDot level="red" />
                                  manquante
                                </span>}
                              </td>
                            ))}
                          </React.Fragment>
                        );
                      }
                      const recalc = offer.qty * offer.pu;
                      if (isDetailed) {
                        const qtyDiff = !isRawBpu && offer.qty !== row.refQty;
                        const adjForEnt = getAdj(row.id, ent.name);
                        const qtyModified = adjForEnt.qty !== undefined;
                        const puModified = adjForEnt.pu !== undefined;
                        const originalOffer = originalOffersByLine.get(row.id)?.[ent.name] ?? offer;
                        const qtyTip = qtyModified
                          ? `Origine : ${fmtNum(originalOffer.qty)}${cellTooltip ? ' · ' + cellTooltip : ''}`
                          : cellTooltip;
                        const puTip = puModified
                          ? `Origine : ${fmt(originalOffer.pu)}${cellTooltip ? ' · ' + cellTooltip : ''}`
                          : cellTooltip;
                        const totalModified = qtyModified || puModified;
                        const originalTotal = originalOffer.qty * originalOffer.pu;
                        const totalTip = totalModified
                          ? `Origine : ${fmt(originalTotal)}${cellTooltip ? ' · ' + cellTooltip : ''}`
                          : cellTooltip;
                        const canEdit = editMode && !isRawBpu;
                        return (
                          <React.Fragment key={ent.name}>
                            <td style={{ ...tdBase, textAlign: 'right', borderLeft: `2px solid ${ent.color}30`, fontVariantNumeric: 'tabular-nums', background: bgCell, color: qtyDiff && !canEdit ? '#D97706' : 'var(--text-secondary)', fontWeight: qtyDiff && !canEdit ? 600 : 400, boxShadow: qtyModified ? 'inset 2px 0 0 #7C3AED' : undefined, padding: canEdit ? '4px 6px' : tdBase.padding }} title={qtyTip}>
                              {isRawBpu ? (
                                <span style={{ color: 'var(--muted)' }}>—</span>
                              ) : canEdit ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>
                                  <input
                                    type="number"
                                    value={offer.qty}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => updateAdjust(row.id, ent.name, { qty: Number(e.target.value) || 0 })}
                                    style={{ width: 52, padding: '2px 4px', border: `1px solid ${qtyModified ? '#7C3AED' : 'var(--border)'}`, borderRadius: 4, fontSize: 12, textAlign: 'right', background: 'var(--bg)', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', outline: 'none' }}
                                  />
                                  {qtyModified && (
                                    <button
                                      onClick={e => { e.stopPropagation(); clearAdjustValue(row.id, ent.name, 'qty'); }}
                                      title={`Revenir à ${fmtNum(originalOffer.qty)}`}
                                      style={{ padding: 0, width: 14, height: 14, border: 'none', borderRadius: 3, background: 'transparent', color: '#7C3AED', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    >
                                      <XMarkIcon style={{ width: 10, height: 10 }} />
                                    </button>
                                  )}
                                  <button
                                    onClick={e => { e.stopPropagation(); setBulkQty(row, offer.qty); }}
                                    title="Appliquer cette quantité à toutes les entreprises"
                                    style={{ padding: 0, width: 18, height: 18, border: '1px solid var(--border)', borderRadius: 3, background: 'var(--bg)', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                  >⇢</button>
                                </span>
                              ) : (
                                fmtNum(offer.qty)
                              )}
                            </td>
                            <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', background: bgCell, boxShadow: puModified ? 'inset 2px 0 0 #7C3AED' : undefined, padding: canEdit ? '4px 6px' : tdBase.padding }} title={puTip}>
                              {canEdit ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={offer.pu}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => updateAdjust(row.id, ent.name, { pu: Number(e.target.value) || 0 })}
                                    style={{ width: 68, padding: '2px 4px', border: `1px solid ${puModified ? '#7C3AED' : 'var(--border)'}`, borderRadius: 4, fontSize: 12, textAlign: 'right', background: 'var(--bg)', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', outline: 'none' }}
                                  />
                                  {puModified && (
                                    <button
                                      onClick={e => { e.stopPropagation(); clearAdjustValue(row.id, ent.name, 'pu'); }}
                                      title={`Revenir à ${fmt(originalOffer.pu)}`}
                                      style={{ padding: 0, width: 14, height: 14, border: 'none', borderRadius: 3, background: 'transparent', color: '#7C3AED', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    >
                                      <XMarkIcon style={{ width: 10, height: 10 }} />
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, justifyContent: 'flex-end' }}>
                                  {bestPuEnt === ent.name && <span title="PU le plus bas sur cette ligne" style={{ fontSize: 9, color: 'var(--muted)' }}>▼</span>}
                                  {worstPuEnt === ent.name && <span title="PU le plus haut sur cette ligne" style={{ fontSize: 9, color: 'var(--muted)' }}>▲</span>}
                                  {fmt(offer.pu)}
                                </span>
                              )}
                            </td>
                            <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, background: bgCell, boxShadow: totalModified ? 'inset 2px 0 0 #7C3AED' : undefined, padding: canEdit ? '4px 6px' : tdBase.padding }} title={totalTip}>
                              {isRawBpu ? (
                                <span style={{ color: 'var(--muted)', fontWeight: 400 }}>—</span>
                              ) : canEdit ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>
                                  {cellLevel && <AlertDot level={cellLevel} />}
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={Math.round(recalc * 100) / 100}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => {
                                      const v = Number(e.target.value);
                                      if (!Number.isFinite(v) || offer.qty <= 0) return;
                                      updateAdjust(row.id, ent.name, { pu: v / offer.qty });
                                    }}
                                    disabled={offer.qty <= 0}
                                    title={offer.qty <= 0 ? 'Quantité nulle — ajustez d\'abord la quantité' : 'Modifie le total (PU recalculé)'}
                                    style={{ width: 88, padding: '2px 4px', border: `1px solid ${totalModified ? '#7C3AED' : 'var(--border)'}`, borderRadius: 4, fontSize: 12, fontWeight: 600, textAlign: 'right', background: 'var(--bg)', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', outline: 'none' }}
                                  />
                                  {puModified && (
                                    <button
                                      onClick={e => { e.stopPropagation(); clearAdjustValue(row.id, ent.name, 'pu'); }}
                                      title={`Revenir à ${fmt(originalTotal)}`}
                                      style={{ padding: 0, width: 14, height: 14, border: 'none', borderRadius: 3, background: 'transparent', color: '#7C3AED', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                    >
                                      <XMarkIcon style={{ width: 10, height: 10 }} />
                                    </button>
                                  )}
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                                  {cellLevel && <AlertDot level={cellLevel} />}
                                  {fmt(recalc)}
                                </span>
                              )}
                            </td>
                            <td style={{ ...tdBase, background: bgCell }} title={cellTooltip}><EcartBar ecart={offer.ecart} /></td>
                          </React.Fragment>
                        );
                      }
                      return (
                        <React.Fragment key={ent.name}>
                          <td style={{ ...tdBase, textAlign: 'right', borderLeft: `2px solid ${ent.color}30`, fontVariantNumeric: 'tabular-nums', fontWeight: 500, background: bgCell }} title={cellTooltip}>
                            {isRawBpu ? (
                              <span style={{ color: 'var(--text-secondary)', fontWeight: 400, display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                                {cellLevel && <AlertDot level={cellLevel} />}
                                {fmt(offer.pu)} <span style={{ fontSize: 10, color: 'var(--muted)' }}>/ unité</span>
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                                {cellLevel && <AlertDot level={cellLevel} />}
                                {fmt(recalc)}
                              </span>
                            )}
                          </td>
                          <td style={{ ...tdBase, background: bgCell }} title={cellTooltip}><EcartBar ecart={offer.ecart} /></td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  {inlineOrphans.map(orphan => {
                    const orphanSelected = panelContent?.type === 'orphan' && panelContent?.data?.id === orphan.id;
                    const orphanBg = orphanSelected ? '#7C3AED08' : '#F59E0B0A';
                    return (
                      <tr
                        key={orphan.id}
                        onClick={() => { if (!matchMode) openOrphanPanel(orphan); }}
                        style={{
                          ['--row-bg' as unknown as string]: orphanBg,
                          cursor: matchMode ? 'default' : 'pointer',
                          borderBottom: '1px solid var(--border)',
                          background: orphanBg,
                          borderLeft: '3px solid #F59E0B',
                        } as React.CSSProperties}
                        onMouseEnter={e => {
                          if (matchMode) return;
                          if (!orphanSelected) {
                            e.currentTarget.style.background = '#F3F4F6';
                            e.currentTarget.style.setProperty('--row-bg', '#F3F4F6');
                          }
                        }}
                        onMouseLeave={e => {
                          if (matchMode) return;
                          e.currentTarget.style.background = orphanBg;
                          e.currentTarget.style.setProperty('--row-bg', orphanBg);
                        }}
                      >
                        <td style={{ ...tdBase, paddingLeft: 10, color: '#F59E0B', fontSize: 13, fontWeight: 700, width: REF_W.art, position: 'sticky', left: REF_L.art, background: 'var(--row-bg)', zIndex: 3 }}>↳</td>
                        <td style={{ ...tdBase, paddingLeft: 8 + (row.level + 1) * 12, fontStyle: 'italic', color: 'var(--text-secondary)', width: REF_W.desig, position: 'sticky', left: REF_L.desig, background: 'var(--row-bg)', zIndex: 3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span style={{ fontSize: 10, color: '#F59E0B', fontWeight: 700, marginRight: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>+ en trop</span>
                          {orphan.designation}
                          {orphan.suggestion && (
                            <span style={{ marginLeft: 8, fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>
                              · match {Math.round(orphan.suggestion.confidence * 100)}%
                            </span>
                          )}
                        </td>
                        <td style={{ ...tdBase, textAlign: 'right', color: 'var(--muted)', width: REF_W.qty, position: 'sticky', left: REF_L.qty, background: 'var(--row-bg)', zIndex: 3 }}>—</td>
                        <td style={{ ...tdBase, textAlign: 'center', color: 'var(--muted)', fontSize: 12, width: REF_W.unit, position: 'sticky', left: REF_L.unit, background: 'var(--row-bg)', zIndex: 3 }}>{orphan.unit}</td>
                        <td style={{ ...tdBase, textAlign: 'right', color: 'var(--muted)', width: REF_W.pu, position: 'sticky', left: REF_L.pu, background: 'var(--row-bg)', zIndex: 3 }}>—</td>
                        <td style={{ ...tdBase, textAlign: 'right', color: 'var(--muted)', width: REF_W.total, position: 'sticky', left: REF_L.total, background: 'var(--row-bg)', zIndex: 3, boxShadow: '2px 0 0 -1px var(--border)' }}>—</td>
                        {visibleEnterprises.map(entCol => {
                          const isOwner = entCol.name === orphan.enterprise;
                          if (matchMode) {
                            const cols = isDetailed ? 4 : 2;
                            const isBeingDragged = dragging?.kind === 'orphan' && dragging.orphanId === orphan.id;
                            return (
                              <td
                                key={entCol.name}
                                colSpan={cols}
                                draggable={isOwner}
                                onDragStart={isOwner ? (e) => {
                                  e.stopPropagation();
                                  const src: DragSource = { kind: 'orphan', orphanId: orphan.id };
                                  setDragging(src);
                                  e.dataTransfer.effectAllowed = 'move';
                                  e.dataTransfer.setData('text/plain', `orphan:${orphan.id}`);
                                } : undefined}
                                onDragEnd={() => { setDragging(null); setDropTargetLineId(null); }}
                                title={isOwner ? `${orphan.designation} — glissez pour réassigner à une ligne réf` : undefined}
                                style={{
                                  ...tdBase,
                                  borderLeft: `2px solid ${entCol.color}30`,
                                  background: 'transparent',
                                  cursor: isOwner ? 'grab' : 'default',
                                  opacity: isBeingDragged ? 0.4 : 1,
                                  padding: '6px 10px',
                                }}
                              >
                                {isOwner && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B', flexShrink: 0 }} />
                                    <span style={{
                                      fontSize: 12, color: 'var(--text-primary)', fontStyle: 'italic',
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      flex: 1, minWidth: 0,
                                    }}>{orphan.designation}</span>
                                  </div>
                                )}
                              </td>
                            );
                          }
                          if (isDetailed) {
                            return (
                              <React.Fragment key={entCol.name}>
                                <td style={{ ...tdBase, textAlign: 'right', borderLeft: `2px solid ${entCol.color}30`, fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                                  {isOwner ? fmtNum(orphan.qty) : ''}
                                </td>
                                <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                  {isOwner ? fmt(orphan.pu) : ''}
                                </td>
                                <td style={{ ...tdBase, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: isOwner ? '#F59E0B' : undefined }}>
                                  {isOwner ? fmt(orphan.total) : ''}
                                </td>
                                <td style={tdBase}></td>
                              </React.Fragment>
                            );
                          }
                          return (
                            <React.Fragment key={entCol.name}>
                              <td style={{ ...tdBase, textAlign: 'right', borderLeft: `2px solid ${entCol.color}30`, fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: isOwner ? '#F59E0B' : undefined }}>
                                {isOwner ? fmt(orphan.total) : ''}
                              </td>
                              <td style={tdBase}></td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--text-primary)', background: 'var(--card-bg)' }}>
                <td colSpan={4} style={{ padding: '14px 10px 14px 12px', fontSize: 11, fontWeight: 700, color: isRawBpu ? 'var(--muted)' : 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', bottom: 0, left: REF_L.art, width: REF_LABEL_W, background: 'var(--card-bg)', zIndex: 9, borderTop: '2px solid var(--text-primary)' }}>
                  {isRawBpu ? 'Catalogue BPU · sélectionnez une DQE pour totaliser' : 'Total recalculé'}
                </td>
                <td style={{ padding: '14px 10px', fontSize: 10, color: 'var(--muted)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.05em', width: REF_W.pu, position: 'sticky', bottom: 0, left: REF_L.pu, background: 'var(--card-bg)', zIndex: 9, borderTop: '2px solid var(--text-primary)' }}>Réf</td>
                <td style={{ padding: '14px 10px', fontSize: 14, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: isRawBpu ? 'var(--muted)' : undefined, width: REF_W.total, position: 'sticky', bottom: 0, left: REF_L.total, background: 'var(--card-bg)', zIndex: 9, borderTop: '2px solid var(--text-primary)', boxShadow: '2px 0 0 -1px var(--border)' }}>
                  {isRawBpu ? '—' : fmt(lotLines.reduce((acc, l) => (!l.isSection && !optionalIds.has(l.id) ? acc + l.refQty * l.refPU : acc), 0))}
                </td>
                {visibleEnterprises.map(ent => {
                  const s = statsByName.get(ent.name);
                  if (!s) return null;
                  const cols = isDetailed ? 4 : 2;
                  if (isRawBpu) {
                    return (
                      <td
                        key={ent.name}
                        colSpan={cols}
                        style={{
                          padding: '10px 12px', borderLeft: `2px solid ${ent.color}40`,
                          background: 'var(--card-bg)',
                          position: 'sticky', bottom: 0, zIndex: 8,
                          borderTop: '2px solid var(--text-primary)',
                        }}
                      >
                        <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'right', fontStyle: 'italic' }}>—</div>
                      </td>
                    );
                  }
                  const mismatch = Math.abs(s.totalDeclare - s.totalRecalcule) > 0.5;
                  const entAdjustCount = Object.values(lineAdjustMap).reduce(
                    (acc, r) => acc + (r[ent.name] ? 1 : 0), 0,
                  );
                  const entHasAdjust = entAdjustCount > 0;
                  const baseTitle = mismatch
                    ? `Total annoncé (bas de l'offre) : ${fmt(s.totalDeclare)} — écart ${s.ecart > 0 ? '+' : ''}${fmt(s.ecart)} (${s.ecartPct > 0 ? '+' : ''}${s.ecartPct.toFixed(2)}%)`
                    : `Total annoncé (bas de l'offre) : ${fmt(s.totalDeclare)}`;
                  const title = entHasAdjust
                    ? `${baseTitle} · ${entAdjustCount} ajustement${entAdjustCount > 1 ? 's' : ''} appliqué${entAdjustCount > 1 ? 's' : ''}`
                    : baseTitle;
                  const entSelected = panelContent?.type === 'enterprise' && panelContent?.data?.name === ent.name;
                  // Rouge pour OAB et total offre faux (irrégularités graves) ; violet si impacté par un ajustement ; neutre sinon.
                  const footerRed = s.isOAB || mismatch;
                  const entBg = entSelected
                    ? '#7C3AED10'
                    : (footerRed ? '#DC262608' : (entHasAdjust ? '#7C3AED08' : 'var(--card-bg)'));
                  return (
                    <td
                      key={ent.name}
                      colSpan={cols}
                      title={title}
                      onClick={() => openEntPanel(ent)}
                      onMouseEnter={e => { if (!entSelected) e.currentTarget.style.background = '#F3F4F6'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = entBg; }}
                      style={{
                        padding: '10px 12px', borderLeft: `2px solid ${ent.color}40`,
                        background: entBg,
                        cursor: 'pointer',
                        position: 'sticky', bottom: 0, zIndex: 8,
                        borderTop: '2px solid var(--text-primary)',
                        boxShadow: entHasAdjust && !footerRed ? 'inset 0 2px 0 #7C3AED' : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        {(mismatch || s.isOAB) && <AlertDot level="red" />}
                        <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: entHasAdjust ? '#7C3AED' : ent.color }}>{fmt(s.totalRecalcule)}</span>
                        {s.isOAB && (
                          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#DC2626', color: '#fff', fontWeight: 700, letterSpacing: '0.08em' }}>OAB</span>
                        )}
                        {entHasAdjust && (
                          <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#7C3AED', color: '#fff', fontWeight: 700, letterSpacing: '0.08em' }} title={`${entAdjustCount} ajustement${entAdjustCount > 1 ? 's' : ''}`}>AJ. {entAdjustCount}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)', textAlign: 'right', marginTop: 3, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                        {s.rank === 1 && <TrophyIcon style={{ width: 11, height: 11, color: '#059669' }} />}
                        <span style={{ fontVariantNumeric: 'tabular-nums', color: s.rank === 1 ? '#059669' : 'var(--text-secondary)', fontWeight: s.rank === 1 ? 700 : 500 }}>
                          Note {s.score.toFixed(1)}/{lotScale} · Rang {s.rank}/{lotEnterprises.length}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
          <div style={{ height: 80 }} />
        </div>

        {/* Panel — toujours monté, animé via translateX */}
        {isOverlay && (
          <div
            onClick={closePanel}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.12)', zIndex: 200,
              opacity: panelOpen ? 1 : 0,
              pointerEvents: panelOpen ? 'auto' : 'none',
              transition: 'opacity 0.25s ease',
            }}
          />
        )}
        <div style={{
          position: 'fixed', top: isOverlay ? 0 : HEADER_H + LOT_TABS_H, right: 0, bottom: 0,
          width: isOverlay ? Math.min(DRAWER_W, windowWidth - 16) : DRAWER_W,
          background: 'var(--panel-bg)', borderLeft: '1px solid var(--border)',
          boxShadow: panelOpen ? (isOverlay ? '-4px 0 24px rgba(0,0,0,0.08)' : '-4px 0 24px rgba(0,0,0,0.06)') : 'none',
          zIndex: isOverlay ? 300 : 150, overflowY: 'auto',
          transform: panelOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          pointerEvents: panelOpen ? 'auto' : 'none',
        }}>
          {panelContent?.type === 'enterprise' && <PanelEnterprise enterprise={panelContent.data} lotOrphans={lotOrphans} stats={statsByName.get(panelContent.data.name)} totalEnterprises={orderedEnterprises.length} isRawBpu={isRawBpu} scale={lotScale} onClose={closePanel} />}
          {panelContent?.type === 'row' && <PanelRow row={panelContent.data} enterprises={displayedEnterprises} optional={isOptional(panelContent.data.id)} onToggleOptional={() => toggleOptional(panelContent.data.id)} isRawBpu={isRawBpu} onClose={closePanel} />}
          {panelContent?.type === 'orphan' && <PanelOrphan orphan={panelContent.data} enterprises={displayedEnterprises} lines={lotLines} onClose={closePanel} />}
        </div>
      </div>
      </>)}

    </div>
  );
};

export default ComparateurMain;
